<template>
  <div class="lectures-page">
    <!-- List View -->
    <div v-if="!aiWorkspace.active" class="lectures-shell">
      <!-- Page Header -->
      <div class="lectures-page-header">
        <div>
          <h4 class="lectures-page-title">{{ $t("Recorded Lectures") }}</h4>
          <p class="lectures-page-subtitle">
            {{ $t("Manage lecture records and publishing details") }}
          </p>
        </div>
        <div class="lectures-header-actions">
          <b-button
            variant="outline-secondary"
            class="filter-toggle-btn"
            @click="showFilters = !showFilters"
          >
            <feather-icon icon="FilterIcon" size="16" />
          </b-button>
          <b-button
            v-if="rights.add"
            variant="gradient-primary"
            @click="openAddLectureModal"
          >
            <feather-icon icon="PlusIcon" class="mr-50" size="16" />
            {{ $t("Add Lecture") }}
          </b-button>
        </div>
      </div>

      <!-- Filters -->
      <b-collapse v-model="showFilters">
        <div class="lectures-filters-panel">
          <b-row class="align-items-center">
            <b-col xl="4" lg="4" md="4" sm="6" cols="12" class="mb-1 mb-lg-0">
              <v-select
                v-model="selectedClass"
                :options="classes"
                :clearable="true"
                :reduce="(val) => val.id"
                label="name"
                :placeholder="$t('Select ' + getLabel('class'))"
                class="v-select lecture-filter-select"
                append-to-body
                @input="onClassFilterChange"
                :disabled="clsLoading"
              />
            </b-col>
            <b-col xl="4" lg="4" md="4" sm="6" cols="12" class="mb-1 mb-lg-0">
              <v-select
                v-model="selectedSection"
                :options="sections"
                :clearable="true"
                :reduce="(val) => val.id"
                label="section"
                :placeholder="$t('Select ' + getLabel('section'))"
                :disabled="!selectedClass"
                class="v-select lecture-filter-select"
                append-to-body
                @input="onFilterChange"
              />
            </b-col>
            <b-col xl="4" lg="4" md="4" sm="6" cols="12" class="mb-1 mb-lg-0">
              <v-select
                v-model="selectedSubject"
                :options="subjects"
                :clearable="true"
                :reduce="(val) => val.id"
                label="subject"
                :placeholder="$t('Select Subject')"
                class="v-select lecture-filter-select"
                append-to-body
                @input="onFilterChange"
              />
            </b-col>
          </b-row>
        </div>
      </b-collapse>

      <!-- Pagination Top -->
      <div class="lectures-pagination-bar">
        <div class="page-size-wrapper">
          <label class="page-size-label">{{ $t("Show") }}:</label>
          <v-select
            :key="'page-size-' + perPage"
            v-model="perPage"
            :options="pageSizeOptions"
            :reduce="(option) => option.value"
            label="label"
            :clearable="false"
            :searchable="false"
            append-to-body
            class="page-size-select-compact"
            @input="onPageSizeChange"
            @option:selected="closePageSizeDropdown"
            ref="pageSizeSelect"
          />
        </div>

        <div class="pagination-count-display">
          <span class="count-text">
            {{ $t("Showing") }} <strong>{{ displayedRange.start }}</strong>
            {{ $t("to") }} <strong>{{ displayedRange.end }}</strong>
            {{ $t("of") }}
            <strong>{{ totalCount }}</strong>
          </span>
        </div>

        <div class="pagination-wrapper-compact">
          <b-pagination
            v-model="currentPage"
            :total-rows="totalCount"
            :per-page="perPage"
            size="sm"
            align="center"
            class="pagination-compact mb-0"
            @change="onPageChange"
          />
        </div>
      </div>

      <!-- Loading -->
      <div v-if="dataLoading" class="lectures-grid">
        <div
          v-for="n in 8"
          :key="'skeleton-' + n"
          class="lecture-card lecture-card--skeleton"
        >
          <div class="lecture-thumb-wrap">
            <div class="lecture-thumb-frame skeleton-block"></div>
          </div>
          <div class="lecture-card-body">
            <div class="skeleton-line skeleton-line--title"></div>
            <div class="skeleton-line skeleton-line--meta"></div>
            <div class="skeleton-line skeleton-line--btn"></div>
          </div>
        </div>
      </div>

      <!-- Empty -->
      <div v-else-if="!lectures.length" class="lectures-empty">
        <feather-icon icon="VideoIcon" size="48" class="text-muted mb-1" />
        <h5>{{ $t("No records found") }}</h5>
        <p class="text-muted mb-2">
          {{ $t("Upload your first recorded lecture to get started.") }}
        </p>
        <b-button
          v-if="rights.add"
          variant="gradient-primary"
          @click="openAddLectureModal"
        >
          <feather-icon icon="PlusIcon" class="mr-50" size="16" />
          {{ $t("Add Lecture") }}
        </b-button>
      </div>

      <!-- Card Grid -->
      <div v-else class="lectures-grid">
        <div
          v-for="item in lectures"
          :key="item.id"
          class="lecture-card"
          :class="{
            'lecture-card--inactive': item.status !== 'active',
            'lecture-card--menu-open': openRegenerateMenuId === item.id,
          }"
        >
          <div class="lecture-thumb-wrap" @click="openVideoPreview(item)">
            <div class="lecture-thumb-frame">
              <video
                v-if="item.videoUrl"
                class="lecture-thumb"
                :src="item.videoUrl"
                preload="metadata"
                muted
              />
              <div v-else class="lecture-thumb lecture-thumb--placeholder">
                <feather-icon icon="VideoIcon" size="32" />
              </div>
            </div>
            <div class="lecture-thumb-overlay">
              <span class="play-badge">
                <feather-icon icon="PlayIcon" size="18" />
              </span>
            </div>
            <span v-if="item.meta_subject" class="subject-pill">
              {{ item.meta_subject }}
            </span>
            <span v-if="item.status !== 'active'" class="status-pill">
              {{ $t("Inactive") }}
            </span>
          </div>

          <div class="lecture-card-body">
            <h5 class="lecture-card-title" :title="item.title">
              {{ item.title || "—" }}
            </h5>

            <div class="lecture-card-meta-grid">
              <div class="lecture-meta-chip">
                <feather-icon icon="BookOpenIcon" size="12" />
                <span>
                  <strong>{{ $t(getLabel("Class")) }}:</strong>
                  {{ formatClassName(item) }}
                </span>
              </div>
              <div class="lecture-meta-chip">
                <feather-icon icon="CalendarIcon" size="12" />
                <span>
                  <strong>{{ $t("Publish") }}:</strong>
                  {{ formatPublishDate(item) }}
                </span>
              </div>
            </div>

            <div v-if="rights.view" class="ai-summary-split" @click.stop>
              <button
                type="button"
                class="ai-summary-main"
                :class="{ 'ai-summary-main--loading': isLectureTranscribing(item.id) }"
                :disabled="isLectureTranscribing(item.id)"
                @click="openAiWorkspace(item)"
              >
                <b-spinner v-if="isLectureTranscribing(item.id)" small />
                <template v-else>
                  <feather-icon icon="ZapIcon" size="15" />
                  {{ $t("AI Summary") }}
                </template>
              </button>
              <div v-if="rights.edit" class="ai-summary-menu-wrap">
                <button
                  type="button"
                  class="ai-summary-dropdown-toggle"
                  :class="{ 'ai-summary-dropdown-toggle--open': openRegenerateMenuId === item.id }"
                  @click.stop="toggleRegenerateMenu(item.id)"
                >
                  <feather-icon icon="ChevronDownIcon" size="16" />
                </button>
                <transition name="ai-summary-menu-fade">
                  <div
                    v-if="openRegenerateMenuId === item.id"
                    class="ai-summary-menu"
                    @click.stop
                  >
                    <button
                      type="button"
                      class="ai-summary-menu-item"
                      @click="handleRegenerateSummary(item)"
                    >
                      <feather-icon icon="RefreshCwIcon" size="14" />
                      {{ $t("Regenerate Summary") }}
                    </button>
                  </div>
                </transition>
              </div>
            </div>

            <div v-if="rights.edit || rights.delete" class="lecture-card-footer">
              <div v-if="rights.edit" class="active-toggle-wrap">
                <b-form-checkbox
                  switch
                  :checked="item.status === 'active'"
                  :disabled="statusUpdatingId === item.id"
                  @change="toggleLectureStatus(item, $event)"
                />
                <span class="active-label">{{ $t("Active") }}</span>
              </div>

              <div class="lecture-card-actions">
                <button
                  v-if="rights.edit"
                  type="button"
                  class="lecture-action-btn lecture-action-btn--edit"
                  v-b-tooltip.hover.top
                  :title="$t('Edit')"
                  :disabled="saving || isLectureTranscribing(item.id)"
                  @click="editLecture(item)"
                >
                  <feather-icon icon="EditIcon" size="16" />
                </button>
                <button
                  v-if="rights.delete"
                  type="button"
                  class="lecture-action-btn lecture-action-btn--delete"
                  v-b-tooltip.hover.top
                  :title="$t('Delete')"
                  :disabled="deletingId === item.id || saving"
                  @click="deleteLecture(item)"
                >
                  <b-spinner v-if="deletingId === item.id" small />
                  <feather-icon v-else icon="Trash2Icon" size="16" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- AI Workspace -->
    <div v-else class="ai-workspace">
      <div class="ai-workspace-topbar">
        <div class="ai-workspace-topbar-left">
          <button type="button" class="ai-workspace-back" @click="closeAiWorkspace">
            <feather-icon icon="ArrowLeftIcon" size="18" />
          </button>
          <div>
            <div class="ai-workspace-title-row">
              <feather-icon icon="ZapIcon" size="18" class="ai-workspace-spark" />
              <h4 class="ai-workspace-title mb-0">
                {{ workspaceLectureTitle }}
              </h4>
            </div>
            <p class="ai-workspace-subtitle mb-0">
              {{ workspaceLectureSubtitle }}
            </p>
          </div>
        </div>
        <button
          type="button"
          class="ai-workspace-ask-btn"
          :class="{ 'ai-workspace-ask-btn--active': !workspaceContentVisible }"
          @click="openChatFocus"
        >
          <feather-icon icon="MessageSquareIcon" size="16" />
          {{ workspaceContentVisible ? $t("Ask AI") : $t("Chat Focus") }}
        </button>
      </div>

      <div v-if="aiWorkspace.loading" class="ai-workspace-loader">
        <div class="ai-workspace-loader-card">
          <div class="ai-loader-ring">
            <b-spinner variant="primary" />
          </div>
          <h5>{{ $t("Generating AI Summary") }}</h5>
          <p class="text-muted mb-0">
            {{ $t("Transcribing lecture audio and preparing your notes...") }}
          </p>
        </div>
      </div>

      <div
        v-else
        class="ai-workspace-layout"
        :class="{
          'ai-workspace-layout--toc-collapsed': !workspaceTocVisible,
          'ai-workspace-layout--content-collapsed': !workspaceContentVisible,
        }"
      >
        <aside
          class="ai-workspace-toc"
          :class="{ 'ai-workspace-toc--collapsed': !workspaceTocVisible }"
        >
          <div class="ai-workspace-toc-head">
            <span>{{ $t("CONTENTS") }}</span>
            <button
              type="button"
              class="ai-panel-toggle-btn"
              v-b-tooltip.hover.top
              :title="workspaceTocVisible ? $t('Hide contents') : $t('Show contents')"
              @click="toggleWorkspaceToc"
            >
              <feather-icon
                :icon="workspaceTocVisible ? 'ChevronLeftIcon' : 'ChevronRightIcon'"
                size="14"
              />
            </button>
          </div>
          <div v-show="workspaceTocVisible" class="ai-workspace-toc-list">
            <button
              v-for="section in workspaceSections"
              :key="section.id"
              type="button"
              class="ai-workspace-toc-item"
              :class="{ 'ai-workspace-toc-item--active': aiWorkspace.selectedSectionId === section.id }"
              @click="selectWorkspaceSection(section.id)"
            >
              {{ section.title }}
            </button>
            <p v-if="!workspaceSections.length" class="ai-workspace-toc-empty">
              {{ $t("No sections found") }}
            </p>
          </div>
        </aside>

        <main
          class="ai-workspace-content"
          :class="{ 'ai-workspace-content--hidden': !workspaceContentVisible }"
          ref="workspaceContent"
        >
          <div class="ai-workspace-content-inner">
            <div class="ai-content-hero">
              <div v-if="workspaceLectureVideoUrl" class="ai-content-video-wrap">
                <video
                  class="ai-content-video"
                  controls
                  :src="workspaceLectureVideoUrl"
                />
              </div>

              <div class="ai-content-hero-text">
                <h2 class="ai-content-title">
                  {{ workspaceLectureTitle }}
                </h2>
                <p v-if="workspaceLectureSubject" class="ai-content-subtitle">
                  {{ workspaceLectureSubject }}
                </p>

                <div class="ai-content-meta-chips">
                  <span v-if="workspaceLectureClass" class="ai-meta-chip">
                    <feather-icon icon="BookOpenIcon" size="13" />
                    <strong>{{ $t(getLabel("Class")) }}:</strong>
                    {{ workspaceLectureClass }}
                  </span>
                  <span v-if="workspaceLecturePublishDate" class="ai-meta-chip">
                    <feather-icon icon="CalendarIcon" size="13" />
                    <strong>{{ $t("Publish") }}:</strong>
                    {{ workspaceLecturePublishDate }}
                  </span>
                </div>
              </div>
            </div>

            <div v-if="aiWorkspace.videoSummary" class="ai-content-summary-card">
              <div class="ai-content-summary-label">
                <feather-icon icon="ZapIcon" size="14" />
                {{ $t("AI Summary") }}
              </div>
              <p class="mb-0">{{ aiWorkspace.videoSummary }}</p>
            </div>

            <div
              v-for="section in workspaceSections"
              :key="'content-' + section.id"
              :id="'workspace-section-' + section.id"
              class="ai-content-section"
              :class="{ 'ai-content-section--active': aiWorkspace.selectedSectionId === section.id }"
            >
              <h3 v-if="section.heading" class="ai-content-section-title">
                <span class="ai-section-dot"></span>
                {{ section.heading }}
              </h3>
              <div class="ai-content-section-body">{{ section.content }}</div>
            </div>

            <div
              v-if="!workspaceSections.length && !aiWorkspace.videoSummary"
              class="ai-workspace-empty-content"
            >
              <feather-icon icon="FileTextIcon" size="42" class="text-muted mb-1" />
              <p class="text-muted mb-0">
                {{ $t("No transcript available for this lecture yet.") }}
              </p>
            </div>
          </div>
        </main>

        <button
          type="button"
          class="ai-workspace-panel-toggle"
          v-b-tooltip.hover.left
          :title="workspaceContentVisible ? $t('Expand chat') : $t('Show lecture notes')"
          @click="toggleWorkspaceContent"
        >
          <feather-icon
            :icon="workspaceContentVisible ? 'ChevronRightIcon' : 'ChevronLeftIcon'"
            size="16"
          />
        </button>

        <aside class="ai-workspace-chat" ref="workspaceChat">
          <div class="ai-workspace-chat-head">
            <div class="ai-workspace-chat-head-left">
              <feather-icon icon="MessageCircleIcon" size="18" />
              <span>{{ $t("AI Chat") }}</span>
            </div>
            <button
              type="button"
              class="ai-panel-toggle-btn ai-panel-toggle-btn--light"
              v-b-tooltip.hover.top
              :title="workspaceContentVisible ? $t('Expand chat') : $t('Show lecture notes')"
              @click="toggleWorkspaceContent"
            >
              <feather-icon
                :icon="workspaceContentVisible ? 'Maximize2Icon' : 'Minimize2Icon'"
                size="14"
              />
            </button>
          </div>

          <div class="ai-workspace-chat-messages" ref="chatMessages">
            <div
              v-for="message in aiWorkspace.chatMessages"
              :key="message.id"
              class="ai-chat-message"
              :class="getChatMessageClasses(message)"
            >
              <div class="ai-chat-avatar">
                <feather-icon
                  :icon="message.role === 'assistant' ? 'ZapIcon' : 'UserIcon'"
                  size="14"
                />
              </div>
              <div class="ai-chat-message-body">
                <div
                  class="ai-chat-bubble"
                  :class="getChatBubbleClasses(message)"
                >
                  {{ message.text }}
                </div>
                <button
                  v-if="
                    message.role === 'assistant' &&
                    (message.audioBase64 || message.audioLoading)
                  "
                  type="button"
                  class="ai-chat-play-btn"
                  :class="{
                    'ai-chat-play-btn--loading': message.audioLoading,
                    'ai-chat-play-btn--active': chatAudioPlayingId === message.id,
                  }"
                  :disabled="message.audioLoading"
                  v-b-tooltip.hover.top
                  :title="
                    chatAudioPlayingId === message.id
                      ? $t('Stop audio')
                      : $t('Play voice reply')
                  "
                  @click="
                    chatAudioPlayingId === message.id
                      ? stopChatAudio()
                      : playChatAudio(message)
                  "
                >
                  <b-spinner v-if="message.audioLoading" small />
                  <feather-icon
                    v-else
                    :icon="chatAudioPlayingId === message.id ? 'VolumeXIcon' : 'Volume2Icon'"
                    size="14"
                  />
                </button>
              </div>
            </div>

            <div v-if="aiWorkspace.chatLoading" class="ai-chat-message ai-chat-message--assistant">
              <div class="ai-chat-avatar">
                <feather-icon icon="ZapIcon" size="14" />
              </div>
              <div class="ai-chat-bubble ai-chat-bubble--typing">
                <span></span><span></span><span></span>
              </div>
            </div>
          </div>

          <div v-if="chatRecording" class="ai-chat-live-caption">
            <feather-icon icon="MicIcon" size="14" class="ai-chat-live-caption-icon" />
            <span v-if="chatLiveTranscript" class="ai-chat-live-caption-text">
              {{ chatLiveTranscript }}
            </span>
            <span v-else class="ai-chat-live-caption-placeholder">
              {{ $t("Listening...") }}
            </span>
            <button
              type="button"
              class="ai-chat-stop-btn"
              v-b-tooltip.hover.top
              :title="$t('Stop recording')"
              @click="cancelVoiceRecording"
            >
              <feather-icon icon="SquareIcon" size="14" />
            </button>
          </div>

          <div class="ai-workspace-chat-input">
            <textarea
              ref="workspaceChatInput"
              v-model="aiWorkspace.chatInput"
              rows="2"
              class="form-control ai-chat-textarea"
              :placeholder="$t('Ask anything about this lecture...')"
              :disabled="aiWorkspace.chatLoading || chatRecording"
              @keydown.enter.exact.prevent="sendWorkspaceChat"
            ></textarea>
            <button
              v-if="chatRecording"
              type="button"
              class="ai-chat-stop-btn ai-chat-stop-btn--compact"
              :disabled="aiWorkspace.chatLoading"
              v-b-tooltip.hover.top
              :title="$t('Stop recording')"
              @click="cancelVoiceRecording"
            >
              <feather-icon icon="SquareIcon" size="16" />
            </button>
            <button
              v-if="!chatRecording"
              type="button"
              class="ai-chat-mic-btn"
              :disabled="aiWorkspace.chatLoading"
              v-b-tooltip.hover.top
              :title="$t('Ask with voice')"
              @click="toggleVoiceRecording"
            >
              <feather-icon icon="MicIcon" size="16" />
            </button>
            <b-button
              variant="gradient-primary"
              class="ai-chat-send-btn"
              :disabled="
                (!aiWorkspace.chatInput.trim() && !chatRecording) ||
                aiWorkspace.chatLoading
              "
              @mousedown.prevent
              @click="sendWorkspaceChat"
            >
              <feather-icon icon="SendIcon" size="16" />
            </b-button>
          </div>
        </aside>
      </div>
    </div>

    <!-- Video Preview Modal -->
    <b-modal
      v-model="showVideoPreview"
      :title="previewLecture.title || $t('Lecture Preview')"
      centered
      size="lg"
      hide-footer
      modal-class="lecture-preview-modal"
    >
      <video
        v-if="previewLecture.videoUrl"
        class="video-preview"
        controls
        autoplay
        :src="previewLecture.videoUrl"
      />
      <p v-else class="text-muted text-center mb-0">
        {{ $t("No video available for this lecture.") }}
      </p>
    </b-modal>

    <!-- Add/Edit Lecture Modal -->
    <b-modal
      v-model="showLectureModal"
      centered
      scrollable
      size="xl"
      no-close-on-backdrop
      modal-class="lecture-form-modal lecture-studio-modal"
      content-class="lecture-studio-content"
      hide-header
      hide-footer
      body-class="p-0"
      @hidden="onLectureModalHidden"
    >
      <div class="lecture-studio-shell">
        <div class="lecture-studio-topbar">
          <div>
            <h4 class="mb-25">
              {{ isEdit ? $t("Edit Lecture") : $t("Upload Lecture") }}
            </h4>
            <p class="text-muted mb-0">
              {{
                isEdit
                  ? $t("Update your video and lecture details")
                  : $t("Add video on the left and fill lecture details on the right")
              }}
            </p>
          </div>
          <button
            type="button"
            class="lecture-modal-close"
            @click="closeLectureModal"
            :disabled="saving"
          >
            <feather-icon icon="XIcon" size="18" />
          </button>
        </div>

        <div class="lecture-studio-body">
          <!-- Left: Video Upload / Preview -->
          <div class="lecture-studio-media">
            <div class="lecture-studio-media-inner">
              <input
                ref="videoFileInput"
                type="file"
                accept="video/*"
                class="d-none"
                @change="onVideoFileSelected"
              />

              <div
                v-if="lectureForm.videoUrl && !videoUpload.selectedFile"
                class="lecture-studio-preview"
              >
                <video
                  :key="lectureForm.videoUrl"
                  class="studio-video-player"
                  controls
                  :src="lectureForm.videoUrl"
                />
                <div class="studio-preview-badge">
                  <feather-icon icon="CheckCircleIcon" size="14" />
                  {{ $t("Video ready") }}
                </div>
              </div>

              <div
                class="video-upload-zone studio-upload-zone"
                :class="{
                  'video-upload-zone--dragging': videoUpload.isDragging,
                  'video-upload-zone--disabled': videoUpload.uploading,
                  'studio-upload-zone--compact':
                    lectureForm.videoUrl && !videoUpload.selectedFile,
                }"
                @dragover.prevent="onVideoDragOver"
                @dragleave.prevent="onVideoDragLeave"
                @drop.prevent="onVideoDrop"
              >
                <template v-if="videoUpload.completed">
                  <video
                    v-if="lectureForm.videoUrl"
                    :key="'completed-' + lectureForm.videoUrl"
                    class="studio-video-player mb-1"
                    controls
                    :src="lectureForm.videoUrl"
                  />
                  <p class="mb-1 font-weight-bold text-success">
                    {{ $t("Video uploaded successfully") }}
                  </p>
                  <b-button
                    variant="outline-light"
                    size="sm"
                    @click="resetVideoUpload"
                    :disabled="videoUpload.uploading"
                  >
                    {{ $t("Replace video") }}
                  </b-button>
                </template>

                <template v-else-if="videoUpload.selectedFile">
                  <div class="studio-upload-file-head">
                    <div>
                      <p class="mb-25 font-weight-bold text-white">
                        {{ videoUpload.selectedFile.name }}
                      </p>
                      <small class="studio-upload-meta">
                        {{ formatFileSize(videoUpload.selectedFile.size) }}
                      </small>
                    </div>
                    <b-button
                      v-if="!videoUpload.uploading"
                      variant="text"
                      size="sm"
                      class="p-0 text-white"
                      @click="resetVideoUpload"
                    >
                      <feather-icon icon="XIcon" size="16" />
                    </b-button>
                  </div>

                  <b-progress
                    :value="videoUpload.percent"
                    :max="100"
                    height="6px"
                    class="mb-50 studio-progress"
                    :variant="videoUpload.paused ? 'warning' : 'primary'"
                  />
                  <div
                    class="d-flex flex-wrap justify-content-between small studio-upload-meta mb-50"
                  >
                    <span>{{ videoUpload.percent.toFixed(1) }}%</span>
                    <span>{{ formatUploadSpeed(videoUpload.speedMbps) }}</span>
                    <span>{{ formatEta(videoUpload.etaSeconds) }}</span>
                  </div>
                  <small class="studio-upload-meta d-block mb-50">
                    {{ $t("Chunks") }}: {{ videoUpload.uploadedChunks }} /
                    {{ videoUpload.totalChunks }}
                  </small>

                  <b-alert
                    v-if="videoUpload.paused"
                    show
                    variant="warning"
                    class="mb-50 py-50 studio-alert"
                  >
                    <small>
                      {{ $t("Upload paused. Missing chunks") }}:
                      {{ videoUpload.missingChunks.join(", ") }}
                    </small>
                  </b-alert>

                  <div class="d-flex flex-wrap gap-50">
                    <b-button
                      v-if="videoUpload.paused"
                      variant="primary"
                      size="sm"
                      @click="resumeVideoUpload"
                      :disabled="videoUpload.uploading"
                    >
                      {{ $t("Resume") }}
                    </b-button>
                    <b-button
                      v-if="videoUpload.uploading"
                      variant="outline-danger"
                      size="sm"
                      @click="cancelVideoUpload"
                    >
                      {{ $t("Cancel upload") }}
                    </b-button>
                  </div>
                </template>

                <template v-else-if="!lectureForm.videoUrl">
                  <div class="studio-upload-empty">
                    <div class="studio-upload-icon-wrap">
                      <feather-icon icon="UploadCloudIcon" size="34" />
                    </div>
                    <h5 class="text-white mb-50">
                      {{ $t("Drag and drop video file") }}
                    </h5>
                    <p class="studio-upload-meta mb-1">
                      {{ $t("MP4, MOV, WEBM and other video formats") }}
                    </p>
                    <b-button
                      variant="primary"
                      @click="triggerVideoFileSelect"
                      :disabled="videoUpload.uploading"
                    >
                      {{ $t("Select Files") }}
                    </b-button>
                  </div>
                </template>

                <template v-else>
                  <p class="text-white mb-50">
                    {{ $t("Replace the current video") }}
                  </p>
                  <b-button
                    variant="outline-light"
                    size="sm"
                    @click="triggerVideoFileSelect"
                    :disabled="videoUpload.uploading"
                  >
                    {{ $t("Upload new video") }}
                  </b-button>
                </template>
              </div>
            </div>
          </div>

          <!-- Right: Lecture Details -->
          <div class="lecture-studio-details">
            <div class="lecture-studio-details-scroll">
              <h6 class="studio-section-label">{{ $t("Details") }}</h6>

              <b-form-group :label="$t('Title') + ' *'" label-class="studio-label">
                <b-form-input
                  v-model="lectureForm.title"
                  class="studio-input"
                  :placeholder="$t('Enter lecture title')"
                />
              </b-form-group>

              <b-form-group :label="$t('Description')" label-class="studio-label">
                <b-form-textarea
                  v-model="lectureForm.description"
                  class="studio-input"
                  :placeholder="$t('Tell viewers about this lecture')"
                  rows="4"
                />
              </b-form-group>

              <h6 class="studio-section-label mt-1">
                {{ $t("Class & Subject") }}
              </h6>

              <b-row>
                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$t('Select ' + getLabel('Class')) + ' *'"
                    label-class="studio-label"
                  >
                    <v-select
                      v-model="lectureForm.classID"
                      :options="classes"
                      :reduce="(val) => val.id"
                      label="name"
                      :placeholder="$t('Select ' + getLabel('class'))"
                      class="studio-select"
                      @input="onModalClassChange"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$t('Select ' + getLabel('Section')) + ' *'"
                    label-class="studio-label"
                  >
                    <v-select
                      v-model="lectureForm.sectionID"
                      :options="modalSections"
                      :reduce="(val) => val.id"
                      label="section"
                      :placeholder="$t('Select ' + getLabel('section'))"
                      class="studio-select"
                      :disabled="!lectureForm.classID"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$t('Subject') + ' *'"
                    label-class="studio-label"
                  >
                    <v-select
                      v-model="lectureForm.subjectID"
                      :options="subjects"
                      :reduce="(val) => val.id"
                      label="subject"
                      :placeholder="$t('Select Subject')"
                      class="studio-select"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$t('Teacher') + ' *'"
                    label-class="studio-label"
                  >
                    <v-select
                      v-model="lectureForm.tID"
                      :options="teachers"
                      :reduce="(val) => val.id"
                      label="name"
                      :placeholder="$t('Select teacher')"
                      class="studio-select"
                      :loading="teachersLoading"
                    />
                  </b-form-group>
                </b-col>
              </b-row>

              <h6 class="studio-section-label mt-1">
                {{ $t("Publishing Schedule") }}
              </h6>

              <b-row>
                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$t('Publish From')"
                    label-class="studio-label"
                  >
                    <flat-pickr
                      v-model="lectureForm.publishFrom"
                      :config="flatPickrConfig"
                      class="form-control studio-input"
                      :placeholder="$t('Select Publish From')"
                    />
                  </b-form-group>
                </b-col>

                <b-col md="6" cols="12">
                  <b-form-group
                    :label="$t('Publish Till')"
                    label-class="studio-label"
                  >
                    <flat-pickr
                      v-model="lectureForm.publishTill"
                      :config="flatPickrConfig"
                      class="form-control studio-input"
                      :placeholder="$t('Select Publish Till')"
                    />
                  </b-form-group>
                </b-col>
              </b-row>
            </div>
          </div>
        </div>

        <div class="lecture-studio-footer">
          <b-button
            variant="outline-secondary"
            @click="closeLectureModal"
            :disabled="saving"
          >
            {{ $t("Cancel") }}
          </b-button>
          <b-button
            variant="gradient-primary"
            @click="saveLecture"
            :disabled="saving || !isFormValid || videoUpload.uploading"
          >
            <b-spinner v-if="saving" small class="mr-50" />
            {{ saving ? $t("Saving") + "..." : isEdit ? $t("Save Changes") : $t("Publish Lecture") }}
          </b-button>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script>
import {
  BRow,
  BCol,
  BButton,
  BPagination,
  VBTooltip,
  BModal,
  BFormGroup,
  BFormInput,
  BFormTextarea,
  BFormCheckbox,
  BSpinner,
  BProgress,
  BAlert,
  BCollapse,
} from "bootstrap-vue";
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import { mapActions } from "vuex";
import ChunkVideoUploader from "@/utils/chunkVideoUpload";
import LiveVoiceCaption from "./liveVoiceCaption";

const AI_MIDDLEWARE_BASE_URL = "https://ai-middleware.myskool.app";
const VIDEO_UPLOAD_BASE_URL = `${AI_MIDDLEWARE_BASE_URL}/`;
const MAX_VOICE_NOTE_BYTES = 25 * 1024 * 1024;

export default {
  name: "LecturesList",
  components: {
    BRow,
    BCol,
    BButton,
    BPagination,
    vSelect,
    BModal,
    BFormGroup,
    BFormInput,
    BFormTextarea,
    BFormCheckbox,
    BSpinner,
    BProgress,
    BAlert,
    BCollapse,
    flatPickr,
  },
  directives: {
    "b-tooltip": VBTooltip,
  },
  data() {
    return {
      reportDomain: this.$store.state.report.replace(/_/g, ""),
      rights: {},
      lectures: [],
      dataLoading: false,
      currentPage: 1,
      perPage: 12,
      totalCount: 0,
      totalPages: 1,
      showFilters: false,
      showVideoPreview: false,
      previewLecture: {},
      aiSummaryLoadingId: null,
      transcribingLectureIds: [],
      transcriptionPromises: {},
      lectureVideoChanged: false,
      openRegenerateMenuId: null,
      aiWorkspace: {
        active: false,
        loading: false,
        lecture: null,
        transcription: "",
        videoSummary: "",
        selectedSectionId: null,
        chatMessages: [],
        chatHistory: [],
        chatInput: "",
        chatLoading: false,
      },
      chatRecording: false,
      liveVoiceCaption: null,
      chatMediaRecorder: null,
      chatRecordedChunks: [],
      chatMediaStream: null,
      chatLiveTranscript: "",
      chatAudioPlayingId: null,
      currentChatAudio: null,
      workspaceTocVisible: true,
      workspaceContentVisible: true,
      pageSizeOptions: [
        { label: "8", value: 8 },
        { label: "12", value: 12 },
        { label: "24", value: 24 },
        { label: "48", value: 48 },
      ],
      classesData: [],
      classes: [],
      sections: [],
      modalSections: [],
      subjects: [],
      teachers: [],
      teachersLoading: false,
      clsLoading: false,
      selectedClass: null,
      selectedSection: null,
      selectedSubject: null,
      showLectureModal: false,
      isEdit: false,
      saving: false,
      statusUpdatingId: null,
      deletingId: null,
      lectureForm: {},
      flatPickrConfig: {
        altFormat: "j M, Y h:i K",
        altInput: true,
        enableTime: true,
        dateFormat: "Y-m-d\\TH:i:S",
      },
      videoUploader: null,
      videoUpload: {
        selectedFile: null,
        uploading: false,
        completed: false,
        paused: false,
        percent: 0,
        speedMbps: 0,
        etaSeconds: null,
        uploadedChunks: 0,
        totalChunks: 0,
        missingChunks: [],
        isDragging: false,
      },
    };
  },
  computed: {
    isHappiness() {
      return this.reportDomain == "myskoolhappinessfoundation";
    },
    displayedRange() {
      if (this.totalCount === 0) return { start: 0, end: 0 };
      const start = (this.currentPage - 1) * this.perPage + 1;
      const end = Math.min(this.currentPage * this.perPage, this.totalCount);
      return { start, end };
    },
    isFormValid() {
      const f = this.lectureForm;
      return (
        f.classID &&
        f.sectionID &&
        f.subjectID &&
        f.tID &&
        f.title &&
        f.title.trim().length > 0
      );
    },
    workspaceSections() {
      const source =
        this.aiWorkspace.transcription ||
        this.aiWorkspace.videoSummary ||
        "";
      return this.parseTranscriptSections(source);
    },
    workspaceLectureTitle() {
      const lecture = this.aiWorkspace.lecture;
      return (lecture && lecture.title) || this.$t("Lecture");
    },
    workspaceLectureSubtitle() {
      const lecture = this.aiWorkspace.lecture;
      return (
        (lecture && lecture.meta_subject) ||
        this.$t("AI powered lecture notes")
      );
    },
    workspaceLectureSubject() {
      const lecture = this.aiWorkspace.lecture;
      return (lecture && lecture.meta_subject) || "";
    },
    workspaceLectureClass() {
      const lecture = this.aiWorkspace.lecture;
      if (!lecture) return "";
      return this.formatClassName(lecture);
    },
    workspaceLecturePublishDate() {
      const lecture = this.aiWorkspace.lecture;
      if (!lecture) return "";
      const formatted = this.formatPublishDate(lecture);
      return formatted === "—" ? "" : formatted;
    },
    workspaceLectureVideoUrl() {
      const lecture = this.aiWorkspace.lecture;
      return (lecture && lecture.videoUrl) || "";
    },
  },
  created() {
    let right = {};
    this.$store.state.menu.forEach((el) => {
      el.children.forEach((ch) => {
        right[ch.route] = {
          view: ch.view,
          add: ch.add,
          edit: ch.edit,
          delete: ch.delete,
        };
      });
    });
    this.$store.commit("setRights", right);

    if (!this.$store.state.rights[this.$route.name]) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }
    this.rights = this.$store.state.rights[this.$route.name];
    if (!this.rights.view) {
      this.$router.replace({ name: "misc-not-authorized" });
      return;
    }

    this.lectureForm = this.getEmptyLecture();
    this.loadClasses();
    this.loadSubjects();
    this.loadTeachers();
    this.loadLectures();
  },
  mounted() {
    document.addEventListener("click", this.closeRegenerateMenu);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.closeRegenerateMenu);
    this.cancelVideoUpload(true);
    this.stopLiveVoiceCaption();
  },
  methods: {
    ...mapActions({ get: "get", post: "post", delete: "delete" }),

    getDefaultVideoUploadState() {
      return {
        selectedFile: null,
        uploading: false,
        completed: false,
        paused: false,
        percent: 0,
        speedMbps: 0,
        etaSeconds: null,
        uploadedChunks: 0,
        totalChunks: 0,
        missingChunks: [],
        isDragging: false,
      };
    },

    resetVideoUploadState() {
      this.videoUpload = this.getDefaultVideoUploadState();
      if (this.$refs.videoFileInput) {
        this.$refs.videoFileInput.value = "";
      }
    },

    triggerVideoFileSelect() {
      if (this.$refs.videoFileInput) {
        this.$refs.videoFileInput.click();
      }
    },

    onVideoDragOver() {
      if (!this.videoUpload.uploading) {
        this.videoUpload.isDragging = true;
      }
    },

    onVideoDragLeave() {
      this.videoUpload.isDragging = false;
    },

    onVideoDrop(event) {
      this.videoUpload.isDragging = false;
      const file = event.dataTransfer?.files?.[0];
      if (file) {
        this.startVideoUpload(file);
      }
    },

    onVideoFileSelected(event) {
      const file = event.target.files?.[0];
      if (file) {
        this.startVideoUpload(file);
      }
    },

    handleVideoUploadProgress(progress) {
      this.videoUpload.percent = progress.percent || 0;
      this.videoUpload.speedMbps = progress.speedMbps || 0;
      this.videoUpload.etaSeconds = progress.etaSeconds;
      this.videoUpload.uploadedChunks = progress.uploadedChunks || 0;
      this.videoUpload.totalChunks = progress.totalChunks || 0;
      this.videoUpload.paused = !!progress.paused;
      this.videoUpload.missingChunks = progress.missingChunks || [];
    },

    async startVideoUpload(file) {
      if (!file.type.startsWith("video/")) {
        this.$bvToast.toast(this.$t("Please select a valid video file."), {
          title: this.$t("Invalid file"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      await this.cancelVideoUpload(true);

      this.videoUploader = new ChunkVideoUploader(VIDEO_UPLOAD_BASE_URL, {
        maxConcurrency: 3,
        maxRetries: 3,
        onProgress: (progress) => this.handleVideoUploadProgress(progress),
      });

      this.videoUpload = {
        ...this.getDefaultVideoUploadState(),
        selectedFile: file,
        uploading: true,
      };

      try {
        const initData = await this.videoUploader.init(file);
        this.videoUpload.totalChunks = initData.totalChunks || 0;
        const result = await this.videoUploader.uploadAll();

        if (result) {
          this.applyVideoUploadResult(result);
          this.videoUpload.completed = true;
          this.videoUpload.paused = false;
          this.$bvToast.toast(this.$t("Video uploaded successfully."), {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });
        }
      } catch (error) {
        if (error && Array.isArray(error.missingChunks)) {
          this.videoUpload.paused = true;
          this.videoUpload.missingChunks = error.missingChunks;
          this.$bvToast.toast(
            `${this.$t("Upload paused. Missing chunks")}: ${error.missingChunks.join(", ")}`,
            {
              title: this.$t("Upload paused"),
              variant: "warning",
              solid: true,
            }
          );
        } else if (!this.videoUploader?.aborted) {
          console.error("Video upload error:", error);
          this.$bvToast.toast(this.$t("Failed to upload video."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
          this.videoUploader = null;
          this.resetVideoUploadState();
        }
      } finally {
        this.videoUpload.uploading = false;
      }
    },

    async resumeVideoUpload() {
      if (!this.videoUploader || !this.videoUpload.paused) return;

      this.videoUpload.uploading = true;
      this.videoUpload.paused = false;

      try {
        const result = await this.videoUploader.resume();
        if (result) {
          this.applyVideoUploadResult(result);
          this.videoUpload.completed = true;
          this.videoUpload.paused = false;
          this.$bvToast.toast(this.$t("Video uploaded successfully."), {
            title: this.$t("Success"),
            variant: "success",
            solid: true,
          });
        }
      } catch (error) {
        if (error && Array.isArray(error.missingChunks)) {
          this.videoUpload.paused = true;
          this.videoUpload.missingChunks = error.missingChunks;
          this.$bvToast.toast(
            `${this.$t("Upload paused. Missing chunks")}: ${error.missingChunks.join(", ")}`,
            {
              title: this.$t("Upload paused"),
              variant: "warning",
              solid: true,
            }
          );
        } else {
          console.error("Resume upload error:", error);
          this.$bvToast.toast(this.$t("Failed to resume upload."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
      } finally {
        this.videoUpload.uploading = false;
      }
    },

    applyVideoUploadResult(result) {
      if (result.url) {
        this.lectureForm.videoUrl = result.url;
      }
      if (result.audioUrl) {
        this.lectureForm.audioUrl = result.audioUrl;
      }
      this.lectureVideoChanged = true;
    },

    async cancelVideoUpload(silent = false) {
      if (this.videoUploader) {
        await this.videoUploader.cancel();
        this.videoUploader = null;
      }
      this.resetVideoUploadState();
      if (!silent) {
        this.$bvToast.toast(this.$t("Video upload cancelled."), {
          title: this.$t("Cancelled"),
          variant: "info",
          solid: true,
        });
      }
    },

    resetVideoUpload() {
      this.videoUploader = null;
      this.resetVideoUploadState();
    },

    closeLectureModal() {
      if (this.videoUpload.uploading) {
        this.$bvToast.toast(this.$t("Please wait for the video upload to finish."), {
          title: this.$t("Upload in progress"),
          variant: "warning",
          solid: true,
        });
        return;
      }
      this.showLectureModal = false;
    },

    onLectureModalHidden() {
      this.cancelVideoUpload(true);
    },

    formatFileSize(bytes) {
      if (!bytes) return "0 B";
      const units = ["B", "KB", "MB", "GB"];
      let size = bytes;
      let unitIndex = 0;
      while (size >= 1024 && unitIndex < units.length - 1) {
        size /= 1024;
        unitIndex += 1;
      }
      return `${size.toFixed(unitIndex === 0 ? 0 : 2)} ${units[unitIndex]}`;
    },

    formatUploadSpeed(speedMbps) {
      if (!speedMbps || speedMbps <= 0) return `${this.$t("Speed")}: —`;
      return `${this.$t("Speed")}: ${speedMbps.toFixed(2)} MB/s`;
    },

    formatEta(etaSeconds) {
      if (etaSeconds == null || !Number.isFinite(etaSeconds)) {
        return `${this.$t("ETA")}: —`;
      }
      const totalSeconds = Math.max(Math.round(etaSeconds), 0);
      const minutes = Math.floor(totalSeconds / 60);
      const seconds = totalSeconds % 60;
      if (minutes > 0) {
        return `${this.$t("ETA")}: ${minutes}m ${seconds}s`;
      }
      return `${this.$t("ETA")}: ${seconds}s`;
    },

    getEmptyLecture() {
      return {
        id: 0,
        campusID: this.$store.state.userData.cId,
        classID: null,
        sectionID: null,
        tID: null,
        subjectID: null,
        title: "",
        description: "",
        videoUrl: "",
        audioUrl: "",
        transcription: "",
        videoSummary: "",
        publishFrom: "",
        publishTill: "",
        status: "active",
        meta_class: "",
        meta_section: "",
        meta_teacher: "",
        meta_subject: "",
        meta_user: "",
        createdBy: 0,
        createdOn: null,
        updatedBy: 0,
        updatedOn: null,
      };
    },

    async loadTeachers() {
      this.teachersLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "staff/filter?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId +
            "&dptID=0&status=present",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        this.teachers = Array.isArray(response) ? response : [];
      } catch (error) {
        console.error("Error loading teachers:", error);
        this.teachers = [];
      } finally {
        this.teachersLoading = false;
      }
    },

    loadModalSectionsFromClass(classId) {
      if (!classId) {
        this.modalSections = [];
        return;
      }
      const selectedClassData = this.classesData.find(
        (item) => item.cls && item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        this.modalSections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section || sec.name || `Section ${sec.id}`,
        }));
      } else {
        this.modalSections = [];
      }
    },

    onModalClassChange() {
      this.lectureForm.sectionID = null;
      this.modalSections = [];
      if (this.lectureForm.classID) {
        this.loadModalSectionsFromClass(this.lectureForm.classID);
      }
    },

    openAddLectureModal() {
      if (!this.rights.add) return;
      this.isEdit = false;
      this.lectureForm = this.getEmptyLecture();
      this.modalSections = [];
      this.lectureVideoChanged = false;
      this.resetVideoUploadState();
      this.videoUploader = null;
      this.showLectureModal = true;
    },

    truncateId(id) {
      if (id == null) return "—";
      const value = String(id);
      if (value.length <= 10) return value;
      return `${value.slice(0, 8)}...`;
    },

    toggleRegenerateMenu(id) {
      this.openRegenerateMenuId =
        this.openRegenerateMenuId === id ? null : id;
    },

    closeRegenerateMenu() {
      this.openRegenerateMenuId = null;
    },

    handleRegenerateSummary(item) {
      if (!this.rights.edit) return;
      this.closeRegenerateMenu();
      this.openAiWorkspace(item, { regenerate: true });
    },

    parseTranscriptSections(text) {
      if (!text || !String(text).trim()) return [];
      const blocks = String(text)
        .trim()
        .split(/\n{2,}/)
        .map((block) => block.trim())
        .filter(Boolean);

      return blocks.map((block, index) => {
        const lines = block.split("\n");
        const firstLine = (lines[0] || "").trim();
        const isHeading =
          lines.length > 1 &&
          firstLine.length <= 90 &&
          (/^#{1,6}\s/.test(firstLine) ||
            /^[\d]+[\.)]\s/.test(firstLine) ||
            /^[A-Z0-9][A-Za-z0-9\s\-—:()]+$/.test(firstLine));
        const title = isHeading
          ? firstLine.replace(/^#{1,6}\s*/, "").replace(/^[\d]+[\.)]\s*/, "")
          : `${this.$t("Section")} ${index + 1}`;
        const content = isHeading
          ? lines.slice(1).join("\n").trim() || firstLine
          : block;

        return {
          id: `section-${index}`,
          title: title.length > 72 ? `${title.slice(0, 69)}...` : title,
          heading: isHeading ? firstLine.replace(/^#{1,6}\s*/, "") : null,
          content,
        };
      });
    },

    getDefaultAiWorkspace() {
      return {
        active: false,
        loading: false,
        lecture: null,
        transcription: "",
        videoSummary: "",
        selectedSectionId: null,
        chatMessages: [],
        chatHistory: [],
        chatInput: "",
        chatLoading: false,
      };
    },

    closeAiWorkspace() {
      this.stopLiveVoiceCaption();
      this.workspaceTocVisible = true;
      this.workspaceContentVisible = true;
      this.aiWorkspace = this.getDefaultAiWorkspace();
      this.aiSummaryLoadingId = null;
    },

    toggleWorkspaceToc() {
      this.workspaceTocVisible = !this.workspaceTocVisible;
    },

    toggleWorkspaceContent() {
      this.workspaceContentVisible = !this.workspaceContentVisible;
      if (!this.workspaceContentVisible) {
        this.$nextTick(() => {
          this.scrollChatToBottom();
          this.focusWorkspaceChat();
        });
      }
    },

    openChatFocus() {
      if (this.workspaceContentVisible) {
        this.workspaceContentVisible = false;
      }
      this.focusWorkspaceChat();
    },

    getAiMiddlewareBaseUrl() {
      return AI_MIDDLEWARE_BASE_URL;
    },

    getWorkspaceChatTopic() {
      return this.aiWorkspace.lecture?.title || this.$t("Lecture");
    },

    getWorkspaceChatSummary() {
      const parts = [];
      if (this.aiWorkspace.videoSummary) {
        parts.push(this.aiWorkspace.videoSummary);
      }
      if (this.aiWorkspace.transcription) {
        parts.push(this.aiWorkspace.transcription);
      }
      return parts.join("\n\n").trim();
    },

    getChatRealtimeLanguage() {
      const locale = (this.$i18n?.locale || "en").toLowerCase();
      if (locale === "ru") return "ru";
      if (locale === "uz") return "uz";
      return "en";
    },

    getChatMessageClasses(message) {
      const classes = [`ai-chat-message--${message.role}`];
      const intent = message.intent || (message.onTopic === false ? "off_topic" : "lesson");
      if (intent === "off_topic" || message.onTopic === false) {
        classes.push("ai-chat-message--off-topic");
      } else if (intent === "greeting") {
        classes.push("ai-chat-message--greeting");
      }
      return classes;
    },

    getChatBubbleClasses(message) {
      const intent = message.intent || (message.onTopic === false ? "off_topic" : "lesson");
      return {
        "ai-chat-bubble--off-topic": intent === "off_topic" || message.onTopic === false,
        "ai-chat-bubble--greeting": intent === "greeting",
      };
    },

    stopLiveVoiceCaption() {
      if (this.liveVoiceCaption) {
        this.liveVoiceCaption.stop();
        this.liveVoiceCaption = null;
      }
      this.cleanupFallbackRecording();
      this.chatRecording = false;
      this.chatLiveTranscript = "";
      this.stopChatAudio();
    },

    async startFallbackRecording() {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = this.getVoiceRecorderMimeType();
      const options = mimeType ? { mimeType } : undefined;
      const recorder = new MediaRecorder(stream, options);

      this.chatRecordedChunks = [];
      this.chatMediaStream = stream;
      this.chatMediaRecorder = recorder;
      this.chatLiveTranscript = "";

      recorder.ondataavailable = (event) => {
        if (event.data && event.data.size > 0) {
          this.chatRecordedChunks.push(event.data);
        }
      };

      recorder.start();
      this.chatRecording = true;
    },

    finishFallbackRecording() {
      return new Promise((resolve) => {
        if (!this.chatMediaRecorder || this.chatMediaRecorder.state === "inactive") {
          this.cleanupFallbackRecording();
          resolve(null);
          return;
        }

        const mimeType = this.getVoiceRecorderMimeType();
        this.chatMediaRecorder.onstop = () => {
          const blob = new Blob(this.chatRecordedChunks, {
            type: mimeType || "audio/webm",
          });
          this.cleanupFallbackRecording();
          resolve(blob.size ? blob : null);
        };
        this.chatMediaRecorder.stop();
      });
    },

    cleanupFallbackRecording() {
      if (this.chatMediaStream) {
        this.chatMediaStream.getTracks().forEach((track) => track.stop());
      }
      this.chatMediaStream = null;
      this.chatMediaRecorder = null;
      this.chatRecordedChunks = [];
    },

    buildChatAssistantMessage(data) {
      const intent =
        data.intent || (data.onTopic === false ? "off_topic" : "lesson");
      return {
        id: Date.now() + Math.random(),
        role: "assistant",
        text: data.replyText || "",
        onTopic: data.onTopic !== false,
        intent,
        audioBase64: null,
        audioMime: "audio/mpeg",
        audioLoading: false,
      };
    },

    async speakChatText(text, voice = "nova") {
      const response = await fetch(`${this.getAiMiddlewareBaseUrl()}/chat/speak`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text, voice }),
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || this.$t("Voice reply failed."));
      }
      return data;
    },

    async loadAssistantVoice(message, text) {
      if (!text || message.intent === "off_topic" || message.onTopic === false) {
        return;
      }

      this.$set(message, "audioLoading", true);
      try {
        const voiceData = await this.speakChatText(text);
        this.$set(message, "audioBase64", voiceData.audioBase64);
        this.$set(message, "audioMime", voiceData.audioMime || "audio/mpeg");
        this.playChatAudio(message);
      } catch (error) {
        console.warn("Speak failed:", error);
      } finally {
        this.$set(message, "audioLoading", false);
      }
    },

    async handleChatApiResponse(data) {
      this.aiWorkspace.chatHistory.push({
        role: "user",
        content: data.userMessage,
      });
      this.aiWorkspace.chatHistory.push({
        role: "assistant",
        content: data.replyText,
      });

      const assistantMessage = this.buildChatAssistantMessage(data);
      this.aiWorkspace.chatMessages.push(assistantMessage);
      this.scrollChatToBottom();

      if (data.replyText) {
        this.loadAssistantVoice(assistantMessage, data.replyText);
      }

      return data;
    },

    async transcribeVoiceBlob(blob) {
      const form = new FormData();
      form.append("voice", blob, "question.webm");
      const response = await fetch(
        `${this.getAiMiddlewareBaseUrl()}/chat/transcribe`,
        {
          method: "POST",
          body: form,
        }
      );
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || this.$t("Transcription request failed."));
      }
      return data.text || "";
    },

    async askWorkspaceChatText(message) {
      const summary = this.getWorkspaceChatSummary();
      if (!summary) {
        throw new Error(
          this.$t("No lesson summary available yet. Please wait for AI summary to load.")
        );
      }

      const response = await fetch(`${this.getAiMiddlewareBaseUrl()}/chat/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: this.getWorkspaceChatTopic(),
          summary,
          message,
          history: this.aiWorkspace.chatHistory,
          includeAudio: false,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || this.$t("Chat request failed."));
      }

      return this.handleChatApiResponse(data);
    },

    async askWorkspaceChatVoice(audioBlob, userTextOverride = null) {
      let userText = (userTextOverride || this.chatLiveTranscript || "").trim();

      if (!userText) {
        if (!audioBlob?.size) {
          throw new Error(this.$t("Voice question"));
        }
        if (audioBlob.size > MAX_VOICE_NOTE_BYTES) {
          throw new Error(
            this.$t("Voice note is too large. Maximum size is 25MB.")
          );
        }
        userText = await this.transcribeVoiceBlob(audioBlob);
      }

      if (!userText) {
        throw new Error(this.$t("Transcription request failed."));
      }

      const summary = this.getWorkspaceChatSummary();
      if (!summary) {
        throw new Error(
          this.$t("No lesson summary available yet. Please wait for AI summary to load.")
        );
      }

      this.aiWorkspace.chatMessages.push({
        id: Date.now(),
        role: "user",
        text: userText,
        onTopic: true,
      });
      this.scrollChatToBottom();

      const response = await fetch(`${this.getAiMiddlewareBaseUrl()}/chat/ask`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          topic: this.getWorkspaceChatTopic(),
          summary,
          message: userText,
          history: this.aiWorkspace.chatHistory,
          includeAudio: false,
        }),
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data?.error || this.$t("Chat request failed."));
      }

      data.userMessage = data.userMessage || userText;
      return this.handleChatApiResponse(data);
    },

    playChatAudio(message) {
      if (!message?.audioBase64) return;

      this.stopChatAudio();

      const mime = message.audioMime || "audio/mpeg";
      const src = `data:${mime};base64,${message.audioBase64}`;
      const audio = new Audio(src);
      this.currentChatAudio = audio;
      this.chatAudioPlayingId = message.id;

      audio.onended = () => {
        if (this.chatAudioPlayingId === message.id) {
          this.chatAudioPlayingId = null;
        }
        this.currentChatAudio = null;
      };

      audio.play().catch((error) => {
        console.warn("Audio playback blocked:", error);
        this.chatAudioPlayingId = null;
        this.currentChatAudio = null;
      });
    },

    stopChatAudio() {
      if (this.currentChatAudio) {
        this.currentChatAudio.pause();
        this.currentChatAudio.currentTime = 0;
        this.currentChatAudio = null;
      }
      this.chatAudioPlayingId = null;
    },

    getVoiceRecorderMimeType() {
      const types = [
        "audio/webm;codecs=opus",
        "audio/webm",
        "audio/mp4",
        "audio/ogg",
      ];
      return types.find((type) => MediaRecorder.isTypeSupported(type)) || "";
    },

    async cancelVoiceRecording() {
      if (!this.chatRecording && !this.liveVoiceCaption && !this.chatMediaRecorder) {
        return;
      }

      this.chatRecording = false;

      if (this.liveVoiceCaption) {
        this.liveVoiceCaption.stop();
        this.liveVoiceCaption = null;
      } else if (this.chatMediaRecorder) {
        await this.cancelFallbackRecording();
      }

      this.clearChatInput();
    },

    cancelFallbackRecording() {
      return new Promise((resolve) => {
        if (!this.chatMediaRecorder || this.chatMediaRecorder.state === "inactive") {
          this.cleanupFallbackRecording();
          resolve();
          return;
        }

        this.chatMediaRecorder.onstop = () => {
          this.cleanupFallbackRecording();
          resolve();
        };
        this.chatMediaRecorder.stop();
      });
    },

    async toggleVoiceRecording() {
      if (this.chatRecording) {
        await this.muteChatMic(true);
        return;
      }
      this.stopChatAudio();
      await this.startChatRecording();
    },

    async muteChatMic(autoSend = false) {
      if (!this.chatRecording && !this.liveVoiceCaption && !this.chatMediaRecorder) {
        return;
      }

      this.chatRecording = false;
      let finalText = "";

      if (this.liveVoiceCaption) {
        finalText = (this.liveVoiceCaption.stop() || this.chatLiveTranscript || "").trim();
        this.liveVoiceCaption = null;
      } else if (this.chatMediaRecorder) {
        const blob = await this.finishFallbackRecording();
        if (blob?.size) {
          try {
            finalText = (await this.transcribeVoiceBlob(blob)).trim();
          } catch (error) {
            console.warn("Transcribe on mute failed:", error);
          }
        }
      }

      this.chatLiveTranscript = "";
      if (finalText) {
        this.aiWorkspace.chatInput = finalText;
      }

      if (autoSend && finalText) {
        await this.sendCurrentMessage();
        return;
      }

      this.$nextTick(() => {
        this.focusWorkspaceChat();
        this.scrollChatToBottom();
      });
    },

    async startChatRecording() {
      if (!navigator.mediaDevices?.getUserMedia) {
        this.$bvToast.toast(this.$t("Voice recording is not supported in this browser."), {
          title: this.$t("Not supported"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      if (!window.RTCPeerConnection) {
        try {
          await this.startFallbackRecording();
        } catch (error) {
          console.error("Microphone access error:", error);
          this.$bvToast.toast(this.$t("Could not access microphone."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
        return;
      }

      try {
        this.chatLiveTranscript = "";
        this.liveVoiceCaption = new LiveVoiceCaption({
          apiBase: this.getAiMiddlewareBaseUrl(),
          language: this.getChatRealtimeLanguage(),
          delay: "low",
          onTextChange: (text) => {
            if (!this.chatRecording) return;
            this.chatLiveTranscript = text;
            this.aiWorkspace.chatInput = text;
            this.scrollChatToBottom();
          },
          onFinalText: (text) => {
            if (!this.chatRecording) return;
            if (text) {
              this.chatLiveTranscript = text;
              this.aiWorkspace.chatInput = text;
              this.scrollChatToBottom();
            }
          },
        });

        await this.liveVoiceCaption.start();
        this.chatRecording = true;
      } catch (error) {
        console.warn("WebRTC live captions unavailable, using fallback:", error);
        this.liveVoiceCaption = null;

        try {
          await this.startFallbackRecording();
        } catch (fallbackError) {
          console.error("Microphone access error:", fallbackError);
          this.stopLiveVoiceCaption();
          this.$bvToast.toast(this.$t("Could not access microphone."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
        }
      }
    },

    pushChatErrorMessage(error) {
      this.aiWorkspace.chatMessages.push({
        id: Date.now() + 1,
        role: "assistant",
        text:
          error?.message ||
          this.$t("Sorry, I couldn't process that right now. Please try again."),
        onTopic: true,
      });
    },

    selectWorkspaceSection(sectionId) {
      this.aiWorkspace.selectedSectionId = sectionId;
      this.$nextTick(() => {
        const el = document.getElementById(`workspace-section-${sectionId}`);
        const container = this.$refs.workspaceContent;
        if (el && container) {
          container.scrollTo({
            top: el.offsetTop - 24,
            behavior: "smooth",
          });
        }
      });
    },

    focusWorkspaceChat() {
      this.$nextTick(() => {
        const input = this.$refs.workspaceChatInput;
        if (input && input.$el) {
          input.$el.focus();
        } else if (input && input.focus) {
          input.focus();
        }
        const chatPanel = this.$refs.workspaceChat;
        if (chatPanel) {
          chatPanel.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }
      });
    },

    scrollChatToBottom() {
      this.$nextTick(() => {
        const container = this.$refs.chatMessages;
        if (container) {
          container.scrollTop = container.scrollHeight;
        }
      });
    },

    initWorkspaceChat() {
      const title = this.aiWorkspace.lecture?.title || this.$t("this lecture");
      this.aiWorkspace.chatHistory = [];
      this.aiWorkspace.chatMessages = [
        {
          id: Date.now(),
          role: "assistant",
          text: `${this.$t("Hi! I'm ready to help you with")} "${title}". ${this.$t("Ask me anything about this lecture.")}`,
          onTopic: true,
        },
      ];
    },

    async fetchAndSaveTranscription(item, options = {}) {
      const opts =
        typeof options === "boolean" ? { force: options } : { ...options };
      const { force = false, silent = false } = opts;

      if (!force && (item.transcription || item.videoSummary)) {
        return item.transcription || item.videoSummary;
      }

      const audioPayload = this.getAudioTranscribePayload(item.audioUrl);
      if (!audioPayload) {
        throw new Error(this.$t("No audio file found for this lecture."));
      }

      const baseUrl = VIDEO_UPLOAD_BASE_URL.replace(/\/$/, "");
      const response = await fetch(`${baseUrl}/transcribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          filename: audioPayload.filename,
          url: audioPayload.url,
        }),
      });

      if (!response.ok) {
        throw new Error(this.$t("Transcription request failed."));
      }

      const data = await response.json();
      const transcription =
        data?.transcription ||
        data?.transcript ||
        data?.text ||
        (typeof data === "string" ? data : "");
      const videoSummary =
        data?.summary ||
        data?.videoSummary ||
        data?.video_summary ||
        "";

      if (!transcription && !videoSummary) {
        throw new Error(this.$t("No transcription returned from the server."));
      }

      const overrides = {};
      if (transcription) overrides.transcription = transcription;
      if (videoSummary) overrides.videoSummary = videoSummary;

      const payload = this.buildSavePayload(item, overrides);
      const saveResponse = await this.post({
        url: this.getSaveUrl(),
        body: payload,
        message: silent
          ? null
          : force
          ? this.$t("Summary regenerated successfully.")
          : this.$t("Transcription saved successfully."),
        token: this.$store.state.userData.token,
        context: this,
        subjects: silent,
      });

      if (saveResponse) {
        if (transcription) item.transcription = transcription;
        if (videoSummary) item.videoSummary = videoSummary;
        this.updateLectureInList(item);
      }

      return transcription || videoSummary;
    },

    updateLectureInList(item) {
      const lectureIndex = this.lectures.findIndex((l) => l.id === item.id);
      if (lectureIndex === -1) return;

      if (item.transcription) {
        this.$set(this.lectures[lectureIndex], "transcription", item.transcription);
      }
      if (item.videoSummary) {
        this.$set(this.lectures[lectureIndex], "videoSummary", item.videoSummary);
      }
      if (item.audioUrl) {
        this.$set(this.lectures[lectureIndex], "audioUrl", item.audioUrl);
      }
    },

    isLectureTranscribing(id) {
      return (
        this.transcribingLectureIds.includes(id) ||
        this.aiSummaryLoadingId === id
      );
    },

    normalizeSavedLecture(payload, response) {
      const saved = { ...payload };
      if (response && typeof response === "object" && !Array.isArray(response)) {
        Object.assign(saved, response);
      } else if (response != null && response !== true && response !== false) {
        saved.id = Number(response) || saved.id;
      }
      return saved;
    },

    shouldAutoTranscribeLecture(lecture) {
      if (!this.getAudioTranscribePayload(lecture?.audioUrl)) return false;
      if (this.lectureVideoChanged) return true;
      return !lecture.transcription && !lecture.videoSummary;
    },

    async waitForLectureTranscription(lectureId) {
      const pending = this.transcriptionPromises[lectureId];
      if (pending) {
        try {
          await pending;
        } catch (error) {
          console.warn("Background transcription failed:", error);
        }
      }
    },

    syncLectureFromList(item) {
      const updated = this.lectures.find((l) => l.id === item.id);
      if (!updated) return item;
      return {
        ...item,
        transcription: updated.transcription || item.transcription || "",
        videoSummary: updated.videoSummary || item.videoSummary || "",
        audioUrl: updated.audioUrl || item.audioUrl || "",
      };
    },

    startBackgroundTranscription(lecture, { force = false } = {}) {
      if (!lecture?.id || !lecture?.audioUrl) return;
      if (this.transcribingLectureIds.includes(lecture.id)) return;

      this.transcribingLectureIds.push(lecture.id);

      const job = this.fetchAndSaveTranscription(lecture, {
        force,
        silent: true,
      })
        .then(() => {
          this.$bvToast.toast(
            this.$t("AI summary is ready for this lecture."),
            {
              title: this.$t("AI Summary Ready"),
              variant: "success",
              solid: true,
            }
          );
        })
        .catch((error) => {
          console.error("Background transcription error:", error);
          this.$bvToast.toast(
            error?.message ||
              this.$t("Failed to generate AI summary in the background."),
            {
              title: this.$t("AI Summary"),
              variant: "warning",
              solid: true,
            }
          );
        })
        .finally(() => {
          this.transcribingLectureIds = this.transcribingLectureIds.filter(
            (id) => id !== lecture.id
          );
          this.$delete(this.transcriptionPromises, lecture.id);
        });

      this.$set(this.transcriptionPromises, lecture.id, job);

      this.$bvToast.toast(
        this.$t("Generating AI summary in the background..."),
        {
          title: this.$t("AI Summary"),
          variant: "info",
          solid: true,
        }
      );
    },

    findSavedLectureInList(savedLecture) {
      if (savedLecture.id) {
        const byId = this.lectures.find((l) => l.id === savedLecture.id);
        if (byId) return byId;
      }

      if (savedLecture.videoUrl) {
        const byVideo = this.lectures.find(
          (l) => l.videoUrl === savedLecture.videoUrl
        );
        if (byVideo) return byVideo;
      }

      return (
        this.lectures.find(
          (l) =>
            l.title === savedLecture.title &&
            l.classID === savedLecture.classID &&
            l.sectionID === savedLecture.sectionID
        ) || null
      );
    },

    async openAiWorkspace(item, options = {}) {
      const regenerate = !!options.regenerate;
      if (!this.rights.view) return;
      if (regenerate && !this.rights.edit) return;
      this.closeRegenerateMenu();

      await this.waitForLectureTranscription(item.id);
      const syncedItem = this.syncLectureFromList(item);

      this.aiSummaryLoadingId = syncedItem.id;
      this.aiWorkspace = {
        ...this.getDefaultAiWorkspace(),
        active: true,
        loading: true,
        lecture: { ...syncedItem },
        transcription: syncedItem.transcription || "",
        videoSummary: syncedItem.videoSummary || "",
      };

      try {
        const needsTranscription =
          regenerate ||
          (!syncedItem.transcription && !syncedItem.videoSummary);

        if (needsTranscription) {
          if (
            !syncedItem.audioUrl &&
            !syncedItem.transcription &&
            !syncedItem.videoSummary
          ) {
            throw new Error(
              this.$t("No audio or transcript available for this lecture.")
            );
          }
          if (syncedItem.audioUrl) {
            const transcription = await this.fetchAndSaveTranscription(
              syncedItem,
              { force: regenerate }
            );
            this.aiWorkspace.transcription =
              syncedItem.transcription || transcription || "";
            this.aiWorkspace.videoSummary = syncedItem.videoSummary || "";
            this.aiWorkspace.lecture = { ...syncedItem };
          }
        }

        this.initWorkspaceChat();
        if (this.workspaceSections.length) {
          this.aiWorkspace.selectedSectionId = this.workspaceSections[0].id;
        }
      } catch (error) {
        console.error("AI workspace error:", error);
        this.$bvToast.toast(
          error?.message || this.$t("Failed to load AI summary."),
          {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          }
        );
        if (
          !this.aiWorkspace.transcription &&
          !this.aiWorkspace.videoSummary
        ) {
          this.closeAiWorkspace();
          return;
        }
        this.initWorkspaceChat();
      } finally {
        this.aiWorkspace.loading = false;
        this.aiSummaryLoadingId = null;
      }
    },

    async sendWorkspaceChat() {
      if (this.aiWorkspace.chatLoading) return;

      if (this.chatRecording) {
        await this.muteChatMic(true);
        return;
      }

      await this.sendCurrentMessage();
    },

    clearChatInput() {
      this.aiWorkspace.chatInput = "";
      this.chatLiveTranscript = "";
    },

    async sendCurrentMessage() {
      if (this.aiWorkspace.chatLoading) return;

      const message = this.aiWorkspace.chatInput.trim();
      if (!message) return;

      this.aiWorkspace.chatMessages.push({
        id: Date.now(),
        role: "user",
        text: message,
        onTopic: true,
      });
      this.clearChatInput();
      this.aiWorkspace.chatLoading = true;
      this.scrollChatToBottom();

      try {
        await this.askWorkspaceChatText(message);
      } catch (error) {
        console.error("AI chat error:", error);
        this.pushChatErrorMessage(error);
      } finally {
        this.aiWorkspace.chatLoading = false;
        this.scrollChatToBottom();
      }
    },

    openVideoPreview(item) {
      if (!item.videoUrl) return;
      this.previewLecture = { ...item };
      this.showVideoPreview = true;
    },

    buildSavePayload(item, overrides = {}) {
      return {
        ...item,
        ...overrides,
        campusID: item.campusID || this.$store.state.userData.cId,
        status: overrides.status || item.status || "active",
        meta_class: "",
        meta_section: "",
        meta_subject: "",
        meta_teacher: "",
        meta_user: "",
      };
    },

    getSaveUrl() {
      return (
        this.$store.state.domain +
        "Lectures/SaveData?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&userID=" +
        this.$store.state.userData.userID
      );
    },

    getDeleteUrl(id) {
      return (
        this.$store.state.domain +
        "Lectures/" +
        id +
        "?db=" +
        this.$store.state.userData.db +
        "&userID=" +
        this.$store.state.userData.userID
      );
    },

    async toggleLectureStatus(item, checked) {
      if (!this.rights.edit) return;
      this.statusUpdatingId = item.id;
      try {
        const payload = this.buildSavePayload(item, {
          status: checked ? "active" : "inactive",
        });
        const response = await this.post({
          url: this.getSaveUrl(),
          body: payload,
          message: checked
            ? this.$t("Lecture activated successfully.")
            : this.$t("Lecture deactivated successfully."),
          token: this.$store.state.userData.token,
          context: this,
        });
        if (response) {
          item.status = checked ? "active" : "inactive";
        }
      } catch (error) {
        console.error("Status update error:", error);
        this.$bvToast.toast(this.$t("Failed to update lecture status."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.statusUpdatingId = null;
      }
    },

    async deleteLecture(item) {
      if (!this.rights.delete) return;
      const confirmed = await this.$bvModal.msgBoxConfirm(
        `${this.$t("Are you sure you want to delete")} "${item.title}"?`,
        {
          title: this.$t("Confirm Delete"),
          okVariant: "danger",
          okTitle: this.$t("Delete"),
          cancelTitle: this.$t("Cancel"),
          centered: true,
        }
      );
      if (!confirmed) return;

      this.deletingId = item.id;
      try {
        const response = await this.delete({
          url: this.getDeleteUrl(item.id),
          message: this.$t("Lecture deleted successfully."),
          token: this.$store.state.userData.token,
          context: this,
        });
        if (response) {
          this.loadLectures();
        }
      } catch (error) {
        console.error("Delete lecture error:", error);
        this.$bvToast.toast(this.$t("Failed to delete lecture."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.deletingId = null;
      }
    },

    getAudioTranscribePayload(audioUrl) {
      if (!audioUrl) return null;
      const trimmed = String(audioUrl).trim();
      if (!trimmed) return null;

      const baseUrl = VIDEO_UPLOAD_BASE_URL.replace(/\/$/, "");
      let url;
      let filename;

      if (/^https?:\/\//i.test(trimmed)) {
        url = trimmed.split("?")[0];
        filename = url.split("/").pop() || trimmed;
      } else {
        filename = trimmed.split("/").pop() || trimmed;
        url = `${baseUrl}/${filename}`;
      }

      return { filename, url };
    },

    editLecture(item) {
      if (!this.rights.edit) return;
      this.isEdit = true;
      this.lectureForm = { ...item };
      this.lectureVideoChanged = false;
      this.modalSections = [];
      this.resetVideoUploadState();
      this.videoUploader = null;
      if (this.lectureForm.classID) {
        this.loadModalSectionsFromClass(this.lectureForm.classID);
      }
      this.showLectureModal = true;
    },

    async saveLecture() {
      if (this.isEdit && !this.rights.edit) return;
      if (!this.isEdit && !this.rights.add) return;
      if (!this.isFormValid) return;
      if (
        this.lectureForm.publishFrom &&
        this.lectureForm.publishTill &&
        new Date(this.lectureForm.publishFrom) >=
          new Date(this.lectureForm.publishTill)
      ) {
        this.$bvToast.toast(this.$t("Publish till must be after publish from."), {
          title: this.$t("Validation Error"),
          variant: "warning",
          solid: true,
        });
        return;
      }

      this.saving = true;
      const shouldTranscribeAfterSave = this.shouldAutoTranscribeLecture(
        this.lectureForm
      );
      const forceTranscription = this.lectureVideoChanged;

      try {
        const payload = this.buildSavePayload(this.lectureForm);

        const obj = {
          url: this.getSaveUrl(),
          body: payload,
          message: this.isEdit
            ? this.$t("Lecture updated successfully.")
            : this.$t("Lecture created successfully."),
          token: this.$store.state.userData.token,
          context: this,
        };

        const response = await this.post(obj);
        if (response) {
          const savedLecture = this.normalizeSavedLecture(payload, response);
          this.showLectureModal = false;
          await this.loadLectures();

          if (shouldTranscribeAfterSave) {
            const listItem = this.findSavedLectureInList(savedLecture);
            const lectureToTranscribe = {
              ...(listItem || {}),
              ...savedLecture,
              id: listItem?.id || savedLecture.id,
              audioUrl: savedLecture.audioUrl || listItem?.audioUrl,
              videoUrl: savedLecture.videoUrl || listItem?.videoUrl,
            };

            if (lectureToTranscribe.id && lectureToTranscribe.audioUrl) {
              this.startBackgroundTranscription(lectureToTranscribe, {
                force: forceTranscription,
              });
            }
          }

          this.lectureVideoChanged = false;
        }
      } catch (error) {
        console.error("Error saving lecture:", error);
        this.$bvToast.toast(this.$t("Failed to save lecture."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.saving = false;
      }
    },

    getLabel(key) {
      const isHappiness = this.isHappiness;
      const map = {
        class: isHappiness ? "group" : "class",
        classes: isHappiness ? "groups" : "classes",
        section: isHappiness ? "level" : "section",
        sections: isHappiness ? "levels" : "sections",
      };
      const result = map[key.toLowerCase()] ?? key.toLowerCase();
      const isCapitalized =
        key[0] === key[0].toUpperCase() && key[0] !== key[0].toLowerCase();
      return isCapitalized
        ? result.charAt(0).toUpperCase() + result.slice(1)
        : result;
    },

    async loadClasses() {
      this.clsLoading = true;
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "myclasses/LoadClasses?db=" +
            this.$store.state.userData.db +
            "&cID=" +
            this.$store.state.userData.cId +
            "&dID=0",
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data =
          response && response.data !== undefined ? response.data : response;
        this.classesData = Array.isArray(data) ? data : [];
        this.classes = this.classesData
          .map((el) => el.cls)
          .filter((cls) => cls && cls.id);
      } catch (error) {
        console.error("Error loading classes:", error);
        this.classes = [];
        this.classesData = [];
      } finally {
        this.clsLoading = false;
      }
    },

    async loadSubjects() {
      try {
        const obj = {
          url:
            this.$store.state.domain +
            "Subjects?db=" +
            this.$store.state.userData.db +
            "&cId=" +
            this.$store.state.userData.cId,
          token: this.$store.state.userData.token,
        };
        const response = await this.get(obj);
        const data =
          response && response.data !== undefined ? response.data : response;
        this.subjects = Array.isArray(data) ? data : [];
      } catch (error) {
        console.error("Error loading subjects:", error);
        this.subjects = [];
      }
    },

    loadSectionsFromClass(classId) {
      if (!classId) {
        this.sections = [];
        return;
      }
      const selectedClassData = this.classesData.find(
        (item) => item.cls && item.cls.id === classId
      );
      if (selectedClassData && selectedClassData.sections) {
        this.sections = selectedClassData.sections.map((sec) => ({
          id: sec.id,
          section: sec.section || sec.name || `Section ${sec.id}`,
        }));
      } else {
        this.sections = [];
      }
    },

    onClassFilterChange() {
      this.selectedSection = null;
      this.sections = [];
      if (this.selectedClass) {
        this.loadSectionsFromClass(this.selectedClass);
      }
      this.currentPage = 1;
      this.loadLectures();
    },

    onFilterChange() {
      this.currentPage = 1;
      this.loadLectures();
    },

    buildLoadUrl() {
      let url =
        this.$store.state.domain +
        "Lectures/LoadData?db=" +
        this.$store.state.userData.db +
        "&cID=" +
        this.$store.state.userData.cId +
        "&page=" +
        this.currentPage +
        "&pageSize=" +
        this.perPage;

      if (this.selectedClass != null) {
        url += "&classID=" + this.selectedClass;
      }
      if (this.selectedSection != null) {
        url += "&sectionID=" + this.selectedSection;
      }
      if (this.selectedSubject != null) {
        url += "&subjectID=" + this.selectedSubject;
      }

      return url;
    },

    async loadLectures() {
      this.dataLoading = true;
      try {
        const obj = {
          url: this.buildLoadUrl(),
          token: this.$store.state.userData.token,
          returnData: true,
        };
        const response = await this.get(obj);
        if (response === false) {
          this.lectures = [];
          this.totalCount = 0;
          this.$bvToast.toast(this.$t("Failed to load lectures."), {
            title: this.$t("Error"),
            variant: "danger",
            solid: true,
          });
          return;
        }
        const payload =
          response && typeof response === "object" ? response : {};
        const container = payload.data !== undefined ? payload.data : payload;
        let records = [];
        if (Array.isArray(container?.records)) {
          records = container.records;
        } else if (Array.isArray(container?.data)) {
          records = container.data;
        } else if (Array.isArray(container)) {
          records = container;
        } else if (Array.isArray(payload)) {
          records = payload;
        } else if (Array.isArray(payload.data)) {
          records = payload.data;
        } else if (Array.isArray(payload.records)) {
          records = payload.records;
        }

        const meta = {
          totalCount:
            payload?.totalCount ??
            container?.totalCount ??
            payload?.data?.totalCount,
          page: payload?.page ?? container?.page,
          pageSize: payload?.pageSize ?? container?.pageSize,
          totalPages: payload?.totalPages ?? container?.totalPages,
        };

        this.lectures = records || [];
        const pageSizeFromApi = Number(meta.pageSize) || this.perPage;
        const totalFromApi = Number(meta.totalCount);
        const totalRecords =
          !Number.isNaN(totalFromApi) && totalFromApi >= 0
            ? totalFromApi
            : records.length;
        this.totalCount = totalRecords;
        if (!Number.isNaN(Number(meta.page)) && Number(meta.page) > 0) {
          this.currentPage = Number(meta.page);
        }
        this.perPage = pageSizeFromApi;
        this.totalPages =
          Number(meta.totalPages) ||
          Math.max(1, Math.ceil(this.totalCount / (this.perPage || 1)));
      } catch (e) {
        console.error("Load lectures error:", e);
        this.lectures = [];
        this.totalCount = 0;
        this.$bvToast.toast(this.$t("Failed to load lectures."), {
          title: this.$t("Error"),
          variant: "danger",
          solid: true,
        });
      } finally {
        this.dataLoading = false;
      }
    },

    formatClassName(item) {
      const className = item.meta_class || "";
      const sectionName = item.meta_section || "";
      if (className && sectionName) {
        return `${className} ${sectionName}`;
      }
      return className || sectionName || "—";
    },

    formatPublishDate(item) {
      const from = item.publishFrom ? this.formatDate(item.publishFrom) : null;
      const till = item.publishTill ? this.formatDate(item.publishTill) : null;

      if (from && till) {
        return `${from} - ${till}`;
      }
      if (from) return from;
      if (till) return till;
      return "—";
    },

    formatDate(dateString) {
      if (!dateString) return "—";
      try {
        const date = new Date(dateString);
        if (isNaN(date.getTime())) return dateString;
        return date.toLocaleDateString("en-US", {
          year: "numeric",
          month: "short",
          day: "numeric",
        });
      } catch (error) {
        return dateString;
      }
    },

    onPageChange() {
      this.loadLectures();
    },

    onPageSizeChange() {
      this.currentPage = 1;
      this.loadLectures();
    },

    closePageSizeDropdown() {
      if (
        this.$refs.pageSizeSelect &&
        this.$refs.pageSizeSelect.toggleDropdown
      ) {
        this.$refs.pageSizeSelect.toggleDropdown(false);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
@import "@core/scss/vue/libs/vue-select.scss";

.lectures-page {
  padding: 0.5rem;
  background: linear-gradient(180deg, #f8f7ff 0%, #f4f6fb 45%, #f8fafc 100%);
  min-height: calc(100vh - 120px);
}

.lectures-shell {
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid rgba(226, 232, 240, 0.9);
  border-radius: 22px;
  padding: 1.35rem 1.5rem 1.5rem;
  box-shadow: 0 20px 50px rgba(99, 102, 241, 0.08);
  backdrop-filter: blur(8px);
}

.lectures-page-header {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.25rem;
}

.lectures-page-title {
  font-size: 1.45rem;
  font-weight: 800;
  color: #111827;
  margin-bottom: 0.2rem;
  letter-spacing: -0.02em;
}

.lectures-page-subtitle {
  margin: 0;
  color: #6b7280;
  font-size: 0.875rem;
}

.lectures-header-actions {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.filter-toggle-btn {
  width: 42px;
  height: 42px;
  padding: 0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 10px;
  border-color: #e5e7eb;
}

.lectures-filters-panel {
  background: #f9fafb;
  border: 1px solid #eef0f4;
  border-radius: 14px;
  padding: 1rem;
  margin-bottom: 1rem;
}

.lecture-filter-select ::v-deep .vs__dropdown-toggle {
  min-height: 42px;
  border-radius: 10px;
  border-color: #e5e7eb;
  background: #fff;
}

.lectures-pagination-bar {
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  padding: 0.75rem 0 1rem;
  border-top: 1px solid #f1f3f7;
  border-bottom: 1px solid #f1f3f7;
  margin-bottom: 1.25rem;
}

.page-size-wrapper {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.page-size-label {
  font-size: 0.8125rem;
  color: #6b7280;
  margin: 0;
  white-space: nowrap;
}

.page-size-select-compact {
  min-width: 80px;
}

.page-size-select-compact ::v-deep .vs__dropdown-toggle {
  height: 34px;
  padding: 0 0.5rem;
  border-radius: 8px;
  border: 1px solid #e5e7eb;
  font-size: 0.8125rem;
}

.pagination-count-display {
  flex: 1;
  text-align: center;

  .count-text {
    font-size: 0.8125rem;
    color: #6b7280;

    strong {
      color: #111827;
    }
  }
}

.lectures-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 1.35rem;
  align-items: stretch;
}

.lecture-card {
  background: #fff;
  border: 1px solid #e8eaf3;
  border-radius: 18px;
  overflow: visible;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.06);
  transition: transform 0.25s ease, box-shadow 0.25s ease, border-color 0.25s ease;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  z-index: 1;

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 18px 40px rgba(99, 102, 241, 0.14);
    border-color: #ddd6fe;
  }
}

.lecture-card--menu-open {
  z-index: 20;
}

.lecture-card--inactive {
  opacity: 0.78;
  filter: grayscale(0.15);
}

.lecture-thumb-wrap {
  position: relative;
  cursor: pointer;
  flex-shrink: 0;
  background: #0f172a;
  overflow: hidden;
  border-radius: 18px 18px 0 0;
}

.lecture-thumb-frame {
  width: 100%;
  height: 168px;
  overflow: hidden;
  background: linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #1e293b 100%);
}

.lecture-thumb {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.35s ease;
}

.lecture-thumb-wrap:hover .lecture-thumb {
  transform: scale(1.04);
}

.lecture-thumb--placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #c4b5fd;
  background: linear-gradient(135deg, #1e1b4b 0%, #4338ca 100%);
}

.lecture-thumb-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    180deg,
    rgba(15, 23, 42, 0.05) 0%,
    rgba(15, 23, 42, 0.55) 100%
  );
  opacity: 0;
  transition: opacity 0.25s ease;
}

.lecture-thumb-wrap:hover .lecture-thumb-overlay {
  opacity: 1;
}

.play-badge {
  width: 48px;
  height: 48px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.95);
  color: #7367f0;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.2);
  transform: scale(0.92);
  transition: transform 0.2s ease;
}

.lecture-thumb-wrap:hover .play-badge {
  transform: scale(1);
}

.subject-pill {
  position: absolute;
  top: 10px;
  right: 10px;
  background: rgba(255, 255, 255, 0.92);
  color: #5b21b6;
  font-size: 0.68rem;
  font-weight: 700;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
  max-width: calc(100% - 20px);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.12);
}

.status-pill {
  position: absolute;
  top: 10px;
  left: 10px;
  background: rgba(17, 24, 39, 0.78);
  color: #fff;
  font-size: 0.68rem;
  font-weight: 600;
  padding: 0.22rem 0.55rem;
  border-radius: 999px;
}

.lecture-card-body {
  padding: 1rem 1.05rem 1.05rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.lecture-card-title {
  font-size: 0.96rem;
  font-weight: 700;
  color: #111827;
  margin-bottom: 0.65rem;
  line-height: 1.4;
  min-height: 2.8em;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.lecture-card-meta-grid {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
  margin-bottom: 0.85rem;
}

.lecture-meta-chip {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  padding: 0.45rem 0.6rem;
  border-radius: 10px;
  background: #f8fafc;
  border: 1px solid #eef2f7;
  font-size: 0.74rem;
  color: #4b5563;
  line-height: 1.4;

  strong {
    color: #374151;
    font-weight: 700;
  }

  svg {
    margin-top: 2px;
    color: #7367f0;
    flex-shrink: 0;
  }
}

.meta-label {
  font-weight: 600;
  color: #374151;
  margin-right: 0.25rem;
}

.ai-summary-split {
  display: flex;
  width: 100%;
  border: 1px solid #ddd6fe;
  border-radius: 12px;
  overflow: visible;
  background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 100%);
  margin-bottom: 0.9rem;
  box-shadow: 0 4px 14px rgba(124, 58, 237, 0.08);
  position: relative;
  z-index: 2;
}

.ai-summary-main {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.62rem 0.8rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.45rem;
  color: #5b21b6;
  font-size: 0.84rem;
  font-weight: 700;
  transition: background 0.2s ease;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.55);
  }

  &:disabled {
    opacity: 0.7;
    cursor: wait;
  }
}

.ai-summary-main--loading {
  background: rgba(255, 255, 255, 0.65);
}

.ai-summary-dropdown-toggle {
  width: 44px;
  height: 100%;
  min-height: 42px;
  border: none;
  border-left: 1px solid #ddd6fe;
  border-radius: 0;
  padding: 0;
  background: transparent;
  color: #6d28d9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover,
  &.ai-summary-dropdown-toggle--open {
    background: rgba(255, 255, 255, 0.55);
  }
}

.ai-summary-menu-wrap {
  position: relative;
  flex-shrink: 0;
}

.ai-summary-menu {
  position: absolute;
  top: calc(100% + 8px);
  right: 0;
  z-index: 30;
  min-width: 210px;
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  box-shadow: 0 14px 36px rgba(15, 23, 42, 0.16);
  padding: 0.35rem;
}

.ai-summary-menu-item {
  width: 100%;
  border: none;
  background: transparent;
  padding: 0.6rem 0.75rem;
  border-radius: 8px;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.84rem;
  font-weight: 600;
  color: #374151;
  text-align: left;
  transition: background 0.15s ease, color 0.15s ease;

  &:hover {
    background: #f5f3ff;
    color: #5b21b6;
  }
}

.ai-summary-menu-fade-enter-active,
.ai-summary-menu-fade-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}

.ai-summary-menu-fade-enter,
.ai-summary-menu-fade-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}

.lecture-card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: auto;
  padding-top: 0.75rem;
  border-top: 1px solid #f1f3f7;
}

.active-toggle-wrap {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.active-label {
  font-size: 0.82rem;
  color: #374151;
  font-weight: 500;
}

.lecture-card-actions {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
}

.lecture-action-btn {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 11px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: transform 0.15s ease, box-shadow 0.15s ease, opacity 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.12);
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.lecture-action-btn--edit {
  background: linear-gradient(135deg, #ede9fe, #ddd6fe);
  color: #6d28d9;
}

.lecture-action-btn--delete {
  background: linear-gradient(135deg, #fee2e2, #fecaca);
  color: #dc2626;
}

/* AI Workspace */
.ai-workspace {
  background: #fff;
  border: 1px solid #e8eaf3;
  border-radius: 22px;
  overflow: hidden;
  box-shadow: 0 24px 60px rgba(99, 102, 241, 0.12);
  height: calc(100vh - 120px);
  max-height: calc(100vh - 120px);
  min-height: 520px;
  display: flex;
  flex-direction: column;
  animation: workspaceFadeIn 0.35s ease;
}

@keyframes workspaceFadeIn {
  from {
    opacity: 0;
    transform: translateY(12px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-workspace-topbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.35rem;
  border-bottom: 1px solid #eef0f4;
  background: linear-gradient(135deg, #ffffff 0%, #faf5ff 55%, #f8fafc 100%);
  flex-shrink: 0;
}

.ai-workspace-topbar-left {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  min-width: 0;
}

.ai-workspace-back {
  width: 40px;
  height: 40px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #fff;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover {
    background: #f3f4f6;
    transform: translateX(-2px);
  }
}

.ai-workspace-title-row {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ai-workspace-spark {
  color: #f59e0b;
}

.ai-workspace-title {
  font-size: 1.15rem;
  font-weight: 700;
  color: #111827;
}

.ai-workspace-subtitle {
  font-size: 0.82rem;
  color: #6b7280;
  margin-top: 0.15rem;
}

.ai-workspace-ask-btn {
  border: none;
  background: linear-gradient(135deg, #7367f0 0%, #9d7aff 100%);
  color: #fff;
  border-radius: 999px;
  padding: 0.55rem 1.1rem;
  font-size: 0.85rem;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  transition: all 0.2s ease;
  box-shadow: 0 8px 20px rgba(115, 103, 240, 0.28);
  flex-shrink: 0;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 12px 28px rgba(115, 103, 240, 0.35);
  }
}

.ai-workspace-ask-btn--active {
  background: linear-gradient(135deg, #5b21b6 0%, #7c3aed 100%);
  box-shadow: 0 10px 24px rgba(91, 33, 182, 0.35);
}

.ai-workspace-loader {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 3rem 1rem;
}

.ai-workspace-loader-card {
  text-align: center;
  max-width: 420px;
}

.ai-loader-ring {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: linear-gradient(135deg, #f5f3ff, #ede9fe);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  animation: pulseRing 1.5s ease-in-out infinite;
}

@keyframes pulseRing {
  0%,
  100% {
    transform: scale(1);
    box-shadow: 0 0 0 0 rgba(115, 103, 240, 0.2);
  }
  50% {
    transform: scale(1.04);
    box-shadow: 0 0 0 12px rgba(115, 103, 240, 0);
  }
}

.ai-workspace-layout {
  flex: 1;
  display: grid;
  grid-template-columns: 240px minmax(0, 1fr) minmax(380px, 420px);
  grid-template-rows: minmax(0, 1fr);
  min-height: 0;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.ai-workspace-toc {
  grid-column: 1;
  grid-row: 1;
  border-right: 1px solid #eef0f4;
  background: linear-gradient(180deg, #fafafa 0%, #f8fafc 100%);
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  overflow: hidden;
}

.ai-workspace-layout--toc-collapsed:not(.ai-workspace-layout--content-collapsed) {
  grid-template-columns: 0 minmax(0, 1fr) minmax(380px, 420px);
}

.ai-workspace-layout--content-collapsed:not(.ai-workspace-layout--toc-collapsed) {
  grid-template-columns: 240px 0 minmax(0, 1fr);
}

.ai-workspace-layout--content-collapsed.ai-workspace-layout--toc-collapsed {
  grid-template-columns: 0 0 minmax(0, 1fr);
}

.ai-workspace-layout--content-collapsed .ai-workspace-chat {
  grid-column: 3;
  grid-row: 1;
  min-width: 0;
  width: 100%;
}

.ai-workspace-layout--content-collapsed.ai-workspace-layout--toc-collapsed .ai-workspace-chat {
  grid-column: 1 / -1;
}

@media (max-width: 1199px) {
  .ai-workspace-layout {
    grid-template-columns: 200px minmax(0, 1fr) minmax(320px, 360px);
  }

  .ai-workspace-layout--toc-collapsed:not(.ai-workspace-layout--content-collapsed) {
    grid-template-columns: 0 minmax(0, 1fr) minmax(320px, 360px);
  }

  .ai-workspace-layout--content-collapsed:not(.ai-workspace-layout--toc-collapsed) {
    grid-template-columns: 200px 0 minmax(0, 1fr);
  }

  .ai-workspace-layout--content-collapsed:not(.ai-workspace-layout--toc-collapsed)
    .ai-workspace-panel-toggle {
    left: 200px;
  }
}

@media (max-width: 991px) {
  .ai-workspace {
    height: auto;
    max-height: none;
    min-height: calc(100vh - 120px);
  }

  .ai-workspace-layout,
  .ai-workspace-layout--toc-collapsed,
  .ai-workspace-layout--content-collapsed,
  .ai-workspace-layout--content-collapsed.ai-workspace-layout--toc-collapsed {
    grid-template-columns: 1fr;
    grid-template-rows: auto minmax(280px, 1fr) minmax(420px, 55vh);
    height: auto;
    overflow: visible;
  }

  .ai-workspace-toc {
    grid-column: 1;
    grid-row: 1;
  }

  .ai-workspace-content {
    grid-column: 1;
    grid-row: 2;
  }

  .ai-workspace-chat {
    grid-column: 1;
    grid-row: 3;
    min-height: 420px;
  }

  .ai-workspace-layout--content-collapsed .ai-workspace-content {
    grid-row: 2;
    max-height: 0;
    overflow: hidden;
  }

  .ai-workspace-layout--content-collapsed .ai-workspace-chat {
    grid-row: 2 / 4;
    min-height: 55vh;
  }

  .ai-workspace-panel-toggle {
    display: none;
  }
}

.ai-workspace-toc--collapsed {
  border-right: none;
  opacity: 0;
  pointer-events: none;
}

.ai-workspace-toc-head {
  padding: 0.9rem 1rem;
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: #6b7280;
  border-bottom: 1px solid #eef0f4;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  flex-shrink: 0;
}

.ai-workspace-toc-list {
  padding: 0.5rem;
  overflow-y: auto;
  flex: 1;
}

.ai-workspace-toc-item {
  width: 100%;
  border: none;
  background: transparent;
  text-align: left;
  padding: 0.62rem 0.8rem;
  border-radius: 10px;
  font-size: 0.82rem;
  color: #4b5563;
  margin-bottom: 0.2rem;
  transition: all 0.18s ease;
  border-left: 3px solid transparent;

  &:hover {
    background: #fff;
    color: #111827;
    box-shadow: 0 4px 12px rgba(15, 23, 42, 0.05);
  }
}

.ai-workspace-toc-item--active {
  background: #fff;
  color: #5b21b6;
  font-weight: 700;
  border-left-color: #7367f0;
  box-shadow: 0 6px 18px rgba(115, 103, 240, 0.12);
}

.ai-workspace-toc-empty {
  padding: 0.75rem;
  font-size: 0.8rem;
  color: #9ca3af;
  margin: 0;
}

.ai-workspace-content {
  overflow-y: auto;
  min-height: 0;
  min-width: 0;
  height: 100%;
  background: linear-gradient(180deg, #ffffff 0%, #fafbff 100%);
  grid-column: 2;
  grid-row: 1;
}

.ai-workspace-content--hidden {
  visibility: hidden;
  width: 0;
  min-width: 0;
  max-width: 0;
  padding: 0;
  margin: 0;
  border: none;
  overflow: hidden;
  opacity: 0;
  pointer-events: none;
}

.ai-workspace-panel-toggle {
  position: absolute;
  top: 50%;
  z-index: 5;
  width: 28px;
  height: 56px;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  background: #fff;
  color: #6d28d9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 8px 20px rgba(15, 23, 42, 0.12);
  transition: left 0.25s ease, right 0.25s ease, background 0.2s ease, color 0.2s ease;
  pointer-events: auto;

  &:hover {
    background: #f5f3ff;
    color: #5b21b6;
  }
}

.ai-workspace-layout:not(.ai-workspace-layout--content-collapsed) .ai-workspace-panel-toggle {
  right: clamp(380px, 32vw, 420px);
  left: auto;
  transform: translate(50%, -50%);
}

.ai-workspace-layout--content-collapsed:not(.ai-workspace-layout--toc-collapsed)
  .ai-workspace-panel-toggle {
  left: 240px;
  right: auto;
  transform: translate(-50%, -50%);
}

.ai-workspace-layout--content-collapsed.ai-workspace-layout--toc-collapsed
  .ai-workspace-panel-toggle {
  left: 0.75rem;
  right: auto;
  transform: translate(0, -50%);
}

.ai-panel-toggle-btn {
  width: 28px;
  height: 28px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.15s ease;

  &:hover {
    background: #f3f4f6;
    color: #5b21b6;
    border-color: #ddd6fe;
  }
}

.ai-panel-toggle-btn--light {
  background: #f5f3ff;
  border-color: #ddd6fe;
  color: #6d28d9;
}

.ai-workspace-content-inner {
  padding: 1.35rem 1.75rem 2rem;
  max-width: 920px;
}

.ai-content-hero {
  margin-bottom: 1.5rem;
}

.ai-content-video-wrap {
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: #0f172a;
  margin-bottom: 1.25rem;
  box-shadow: 0 16px 40px rgba(15, 23, 42, 0.18);
  border: 1px solid #e5e7eb;
}

.ai-content-video {
  width: 100%;
  height: 280px;
  object-fit: cover;
  display: block;
  background: #000;
}

.ai-content-hero-text {
  padding-bottom: 0.25rem;
}

.ai-content-title {
  font-size: 1.65rem;
  font-weight: 800;
  color: #1e3a5f;
  margin-bottom: 0.35rem;
  line-height: 1.25;
  letter-spacing: -0.02em;
}

.ai-content-subtitle {
  font-size: 0.95rem;
  color: #6b7280;
  font-style: italic;
  margin-bottom: 0.85rem;
}

.ai-content-meta-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 0.55rem;
}

.ai-meta-chip {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.45rem 0.75rem;
  border-radius: 999px;
  background: #fff;
  border: 1px solid #e5e7eb;
  font-size: 0.8rem;
  color: #4b5563;
  box-shadow: 0 4px 12px rgba(15, 23, 42, 0.04);

  strong {
    color: #374151;
  }

  svg {
    color: #7367f0;
  }
}

.ai-content-summary-card {
  background: linear-gradient(135deg, #faf5ff 0%, #f5f3ff 55%, #eef2ff 100%);
  border: 1px solid #ddd6fe;
  border-radius: 16px;
  padding: 1.1rem 1.2rem;
  margin-bottom: 1.5rem;
  line-height: 1.7;
  color: #374151;
  box-shadow: 0 10px 28px rgba(124, 58, 237, 0.08);
}

.ai-content-section {
  margin-bottom: 1rem;
  scroll-margin-top: 24px;
  background: #fff;
  border: 1px solid #eef0f4;
  border-radius: 16px;
  padding: 1.15rem 1.25rem;
  border-left: 4px solid #c4b5fd;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.04);
  transition: border-color 0.2s ease, box-shadow 0.2s ease;
}

.ai-content-section--active {
  border-left-color: #7367f0;
  box-shadow: 0 10px 28px rgba(115, 103, 240, 0.1);
}

.ai-content-section-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: #1e3a5f;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.ai-section-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #7367f0;
  flex-shrink: 0;
}

.ai-content-section-body {
  font-size: 0.94rem;
  line-height: 1.8;
  color: #374151;
  white-space: pre-wrap;
}

.ai-content-summary-label {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.78rem;
  font-weight: 700;
  color: #7c3aed;
  margin-bottom: 0.5rem;
}

.ai-workspace-empty-content {
  text-align: center;
  padding: 3rem 1rem;
  border: 1px dashed #ddd6fe;
  border-radius: 16px;
  background: #faf5ff;
}

.ai-workspace-chat {
  grid-column: 3;
  grid-row: 1;
  border-left: 1px solid #eef0f4;
  background: linear-gradient(180deg, #fcfcfd 0%, #f8fafc 100%);
  display: flex;
  flex-direction: column;
  min-height: 0;
  min-width: 0;
  height: 100%;
  overflow: hidden;
}

.ai-workspace-chat-head {
  padding: 0.9rem 1rem;
  border-bottom: 1px solid #eef0f4;
  font-size: 0.92rem;
  font-weight: 800;
  color: #111827;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
  background: #fff;
  flex-shrink: 0;
}

.ai-workspace-chat-head-left {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-width: 0;
}

.ai-workspace-chat-messages {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.9rem;
  background:
    radial-gradient(circle at top right, rgba(115, 103, 240, 0.06), transparent 40%),
    linear-gradient(180deg, #fafbff 0%, #f8fafc 100%);

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d1d5db;
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #9ca3af;
  }
}

.ai-chat-message {
  display: flex;
  gap: 0.55rem;
  align-items: flex-start;
  animation: chatIn 0.25s ease;
}

@keyframes chatIn {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.ai-chat-message--user {
  flex-direction: row-reverse;

  .ai-chat-bubble {
    background: linear-gradient(135deg, #7367f0 0%, #9d7aff 100%);
    color: #fff;
    border-bottom-right-radius: 4px;
    box-shadow: 0 8px 20px rgba(115, 103, 240, 0.22);
  }

  .ai-chat-avatar {
    background: #ede9fe;
    color: #6d28d9;
  }
}

.ai-chat-message--assistant {
  .ai-chat-bubble {
    background: #fff;
    border: 1px solid #eef0f4;
    border-bottom-left-radius: 4px;
    box-shadow: 0 6px 16px rgba(15, 23, 42, 0.05);
  }

  .ai-chat-avatar {
    background: linear-gradient(135deg, #fef3c7, #fde68a);
    color: #d97706;
  }
}

.ai-chat-message--off-topic {
  .ai-chat-bubble--off-topic {
    background: #fffbeb;
    border-color: #fde68a;
    color: #92400e;
  }
}

.ai-chat-message--greeting {
  .ai-chat-bubble--greeting {
    background: #f0fdf4;
    border-color: #bbf7d0;
    color: #166534;
  }
}

.ai-chat-live-caption {
  display: flex;
  align-items: center;
  gap: 0.45rem;
  margin: 0 0.85rem 0.55rem;
  padding: 0.55rem 0.7rem;
  border-radius: 10px;
  background: #fffbeb;
  border: 1px dashed #fcd34d;
  color: #92400e;
  font-size: 0.84rem;
  line-height: 1.45;
  min-height: 2rem;
}

.ai-chat-live-caption-text {
  flex: 1;
  min-width: 0;
  word-break: break-word;
}

.ai-chat-stop-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.3rem;
  flex-shrink: 0;
  border: none;
  border-radius: 8px;
  background: #fee2e2;
  color: #b91c1c;
  font-size: 0.78rem;
  font-weight: 600;
  padding: 0.35rem 0.55rem;
  cursor: pointer;
  transition: background 0.15s ease;

  &:hover:not(:disabled) {
    background: #fecaca;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.ai-chat-stop-btn--compact {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 14px;
  border: 1px solid #fca5a5;
}

.ai-chat-live-caption-icon {
  flex-shrink: 0;
  margin-top: 0.1rem;
  color: #d97706;
}

.ai-chat-live-caption-placeholder {
  color: #b45309;
  font-style: italic;
}

.ai-chat-play-btn--loading {
  cursor: default;
}

.ai-chat-message-body {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  max-width: 85%;
}

.ai-chat-message--user .ai-chat-message-body {
  align-items: flex-end;
}

.ai-chat-play-btn {
  border: none;
  background: #ede9fe;
  color: #6d28d9;
  width: 28px;
  height: 28px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, transform 0.15s ease;

  &:hover {
    background: #ddd6fe;
    transform: scale(1.05);
  }
}

.ai-chat-play-btn--active {
  background: #fecaca;
  color: #b91c1c;

  &:hover {
    background: #fca5a5;
  }
}

.ai-chat-avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.ai-chat-bubble {
  max-width: 100%;
  padding: 0.65rem 0.85rem;
  border-radius: 14px;
  font-size: 0.86rem;
  line-height: 1.55;
  white-space: pre-wrap;
}

.ai-chat-bubble--typing {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  min-width: 56px;

  span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #9ca3af;
    animation: typingDot 1.2s infinite ease-in-out;

    &:nth-child(2) {
      animation-delay: 0.15s;
    }

    &:nth-child(3) {
      animation-delay: 0.3s;
    }
  }
}

@keyframes typingDot {
  0%,
  80%,
  100% {
    transform: translateY(0);
    opacity: 0.4;
  }
  40% {
    transform: translateY(-4px);
    opacity: 1;
  }
}

.ai-workspace-chat-input {
  padding: 0.9rem;
  border-top: 1px solid #eef0f4;
  display: flex;
  gap: 0.55rem;
  align-items: flex-end;
  background: #fff;
  box-shadow: 0 -8px 24px rgba(15, 23, 42, 0.04);
  flex-shrink: 0;
}

.ai-chat-textarea {
  flex: 1;
  border-radius: 14px !important;
  border-color: #e5e7eb !important;
  resize: none;
  font-size: 0.86rem;
  background: #fafafa !important;
}

.ai-chat-mic-btn {
  width: 44px;
  height: 44px;
  border: 1px solid #e5e7eb;
  border-radius: 14px;
  background: #fff;
  color: #6d28d9;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f5f3ff;
    border-color: #ddd6fe;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
}

.ai-chat-mic-btn--recording {
  background: #fee2e2;
  border-color: #fca5a5;
  color: #dc2626;
  animation: micPulse 1.2s ease-in-out infinite;
}

@keyframes micPulse {
  0%,
  100% {
    box-shadow: 0 0 0 0 rgba(220, 38, 38, 0.25);
  }
  50% {
    box-shadow: 0 0 0 8px rgba(220, 38, 38, 0);
  }
}

.ai-chat-send-btn {
  width: 44px;
  height: 44px;
  padding: 0;
  border-radius: 14px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 8px 18px rgba(115, 103, 240, 0.25);
}

.lectures-empty {
  text-align: center;
  padding: 3rem 1rem;
  border: 1px dashed #dfe3ea;
  border-radius: 16px;
  background: #fafafa;
}

.lecture-card--skeleton {
  pointer-events: none;

  .lecture-thumb-wrap {
    background: #f3f4f6;
  }

  .lecture-thumb-frame {
    height: 168px;
  }

  .skeleton-block {
    height: 168px;
    border-radius: 18px 18px 0 0;
  }
}

.skeleton-block,
.skeleton-line {
  background: linear-gradient(90deg, #f3f4f6, #e5e7eb, #f3f4f6);
  background-size: 200% 100%;
  animation: shimmer 1.2s ease-in-out infinite;
}

.skeleton-line {
  height: 12px;
  border-radius: 6px;
  margin-bottom: 0.65rem;
}

.skeleton-line--title {
  width: 85%;
  height: 16px;
}

.skeleton-line--meta {
  width: 60%;
}

.skeleton-line--btn {
  width: 100%;
  height: 36px;
  margin-bottom: 0;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

.lecture-modal-close {
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 10px;
  background: #f3f4f6;
  color: #374151;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.lecture-studio-shell {
  display: flex;
  flex-direction: column;
  max-height: min(760px, calc(100vh - 4rem));
}

.lecture-studio-topbar {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 1rem;
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid #f1f3f7;
}

.lecture-studio-body {
  display: grid;
  grid-template-columns: minmax(320px, 42%) 1fr;
  flex: 1;
  min-height: 0;
  overflow: hidden;
}

@media (max-width: 991px) {
  .lecture-studio-body {
    grid-template-columns: 1fr;
    overflow: auto;
  }
}

.lecture-studio-media {
  background: #0f0f0f;
  border-right: 1px solid #1f1f1f;
  min-height: 360px;
}

.lecture-studio-media-inner {
  height: 100%;
  display: flex;
  flex-direction: column;
  padding: 1rem;
  gap: 0.75rem;
}

.lecture-studio-preview {
  position: relative;
  border-radius: 12px;
  overflow: hidden;
  background: #000;
}

.studio-video-player {
  width: 100%;
  max-height: 280px;
  display: block;
  background: #000;
  border-radius: 12px;
}

.studio-preview-badge {
  position: absolute;
  top: 10px;
  left: 10px;
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  background: rgba(16, 185, 129, 0.92);
  color: #fff;
  font-size: 0.72rem;
  font-weight: 600;
  padding: 0.25rem 0.55rem;
  border-radius: 999px;
}

.studio-upload-zone {
  flex: 1;
  min-height: 260px;
  border: 1px dashed rgba(255, 255, 255, 0.18);
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  text-align: center;
}

.studio-upload-zone--compact {
  min-height: 120px;
  flex: 0;
}

.studio-upload-zone.video-upload-zone--dragging {
  border-color: #7367f0;
  background: rgba(115, 103, 240, 0.12);
}

.studio-upload-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.studio-upload-icon-wrap {
  width: 72px;
  height: 72px;
  border-radius: 50%;
  background: rgba(115, 103, 240, 0.18);
  color: #a78bfa;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0.75rem;
}

.studio-upload-meta {
  color: rgba(255, 255, 255, 0.62);
}

.studio-upload-file-head {
  width: 100%;
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 0.75rem;
  text-align: left;
}

.studio-progress {
  width: 100%;
}

.studio-alert {
  width: 100%;
  text-align: left;
}

.lecture-studio-details {
  background: #fff;
  min-height: 0;
  overflow: hidden;
}

.lecture-studio-details-scroll {
  height: 100%;
  overflow-y: auto;
  padding: 1.25rem 1.5rem;
}

.studio-section-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #6b7280;
  margin-bottom: 0.85rem;
}

.studio-label {
  font-size: 0.82rem;
  font-weight: 600;
  color: #374151;
}

.studio-input {
  border-radius: 10px;
  border-color: #e5e7eb;
}

.studio-select ::v-deep .vs__dropdown-toggle {
  min-height: 42px;
  border-radius: 10px;
  border-color: #e5e7eb;
}

.lecture-studio-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem 1.25rem;
  border-top: 1px solid #f1f3f7;
  background: #fff;
}

.video-upload-zone {
  transition: border-color 0.2s ease, background 0.2s ease;
}

.video-upload-zone--disabled {
  opacity: 0.85;
}

.video-preview {
  width: 100%;
  max-height: 320px;
  border-radius: 12px;
  background: #000;
  display: block;
}

.gap-50 {
  gap: 0.5rem;
}
</style>

<style lang="scss">
@import "@core/scss/vue/libs/vue-flatpicker.scss";

.lecture-studio-modal {
  &.modal {
    display: flex !important;
    align-items: center !important;
    justify-content: center !important;
    padding: 1.25rem;
  }

  .modal-dialog {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 1100px !important;
    margin: 0 auto !important;
    min-height: calc(100% - 2.5rem);
  }

  .modal-content,
  .lecture-studio-content {
    width: 100%;
    border: none;
    border-radius: 18px;
    overflow: hidden;
    box-shadow: 0 24px 60px rgba(15, 23, 42, 0.22);
  }

  .modal-body {
    padding: 0;
  }
}

.lecture-form-modal .modal-content {
  border: none;
  border-radius: 18px;
  overflow: hidden;
}

.lecture-preview-modal .modal-content {
  border: none;
  border-radius: 16px;
}
</style>
